import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  ActivityIndicator,
  Animated,
  AppState,
  FlatList,
  Image,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  type ImageStyle,
  StyleSheet,
  type StyleProp,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  useWindowDimensions,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';
import { GestureDetector, type NativeGesture } from 'react-native-gesture-handler';
import Icon from 'react-native-vector-icons/Feather';
import {
  addDoc,
  arrayRemove,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { showMessage } from 'react-native-flash-message';
import { useChats, useChatMessages, sendChatMessage, deleteChatForUser, markChatReadForUser, muteChatForUser, unmuteChatForUser, useSingleChatMuted, useIncomingVibeRequests, useSentVibeRequests, sendVibeRequest, acceptVibeRequest, declineVibeRequest, cancelVibeRequest, useChatLastReadAt, useChatDocExists, logChatEnvDebug, getVibePairKey, useMutedChatIds } from '../hooks/useProfile';
import { setActiveChatId } from '../hooks/useChatNotifications';
import { useOwnedPartnerVenues } from '../hooks/useOwnedPartnerVenues';
import { useAdminAllPendingRequests } from '../hooks/usePlanJoinRequest';
import LoadingSpinner from '../components/LoadingSpinner';
import TonyteBackground from '../components/TonyteBackground';
import TonyteModal from '../components/TonyteModal';
import VibeVerifiedBadge from '../components/VibeVerifiedBadge';
import { Colors, Typography, Spacing, Radius } from '../theme';
import type { ChatItem, ChatMessage, ChatReaction, MessagesTabFocus, RootStackParamList, TonyteActivity, VenueSummary, VibeRequest } from '../types';
import { auth, DEFAULT_COVER, firestore, FIRESTORE_COLLECTIONS, PUBLIC_ACTIVITY_COLLECTIONS, uploadChatAudio, uploadChatImage } from '../firebase';
import { TONYTE_PROFILE_IMAGE, getUserDisplayName, getUserProfile, publicBio, useUserProfile, type PublicUserProfile } from '../utils/userProfiles';
import { blockUserFromChat } from '../utils/blocking';
import { useI18n } from '../i18n';
import { translateVisibleChatMessages } from '../utils/translations';
import { formatRelativeTime, formatSeenReceipt, formatDateSeparatorLabel, shouldGroupWithPrevious, isSameCalendarDay, toEpochMs } from '../utils/chatFormatting';
import ChatHeader from '../components/chat/ChatHeader';
import ChatAtmosphere from '../components/chat/ChatAtmosphere';
import ChatMessageBubble from '../components/chat/ChatMessageBubble';
import ChatInputBar from '../components/chat/ChatInputBar';
import GifPickerBottomSheet from '../components/chat/GifPickerBottomSheet';
import VoiceRecorderComposer from '../components/chat/VoiceRecorderComposer';
import DateSeparatorPill from '../components/chat/DateSeparatorPill';
import MemberEventNotice from '../components/chat/MemberEventNotice';
import { postEventChatMemberNotice } from '../utils/activityActions';
import TypingIndicator from '../components/chat/TypingIndicator';
import ReactionBar from '../components/chat/ReactionBar';
import { useChatReactions, toggleMessageReaction } from '../hooks/useChatReactions';
import { useOtherUsersTyping, useTypingIndicator } from '../hooks/useTypingIndicator';
import { useUserPresence } from '../hooks/usePresenceHeartbeat';
import { useKeyboardInsets } from '../hooks/useKeyboardInsets';
import { checkMicrophonePermissionSilently, pickImageFromLibrary, requestMicrophonePermission, takePhotoWithCamera } from '../utils/permissions';
import { getLocationSnapshot } from '../hooks/useLocation';
import type { ChatGif } from '../services/ChatGifService';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import { isEventExpired as isActivityExpired } from '../utils/activityDates';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { daylightStyle } from '../theme/daylightStyle';
import { getPlanMemberIds, removePlanMember } from '../utils/planMembership';
import { leavePartnerCommunity, listPartnerCommunityMembers, reinstatePartnerCommunityMember, removePartnerCommunityMember, setPartnerCommunityCoAdmin, updatePartnerCommunityDescription, usePartnerCommunities, type PartnerCommunityRole } from '../services/partnerCommunities';
import { resolvePartnerIdentity, visiblePartnerVenues } from '../utils/partnerIdentity';

// Shared empty value so bubbles without reactions keep a stable prop (memo).
const NO_REACTIONS: ChatReaction[] = [];

// ─── Helpers ────────────────────────────────────────────────────────────────

const BADGE_STYLES: Record<string, { bg: string; text: string; border: string; label: string }> = {
  event: { bg: 'rgba(242,125,38,0.15)', text: '#F27D26', border: 'rgba(242,125,38,0.2)', label: 'EVENT' },
  plan:  { bg: 'rgba(38,208,206,0.15)', text: '#26D0CE', border: 'rgba(38,208,206,0.2)',  label: 'PLAN'  },
  match: { bg: 'rgba(255,255,255,0.10)', text: 'rgba(255,255,255,0.5)', border: 'rgba(255,255,255,0.10)', label: 'MATCH' },
  vibe:  { bg: 'rgba(38,208,206,0.12)', text: '#26D0CE', border: 'rgba(38,208,206,0.25)', label: 'VIBE'  },
  community: { bg: 'rgba(139,92,246,0.15)', text: '#A78BFA', border: 'rgba(139,92,246,0.3)', label: 'COMMUNITY' },
};

const ProfileImage: React.FC<{ uri?: string; style: StyleProp<ImageStyle> }> = ({ uri, style }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [uri]);

  return (
    <Image
      source={uri && !failed ? { uri } : TONYTE_PROFILE_IMAGE}
      style={style}
      resizeMode="cover"
      onError={() => setFailed(true)}
    />
  );
};

// A user's current profile picture, falling back to a stored snapshot
// (message senderPhotoURL, People Here participant doc) while it loads.
const LiveProfileImage: React.FC<{ userId?: string | null; fallbackUri?: string; style: StyleProp<ImageStyle> }> = ({ userId, fallbackUri, style }) => {
  const profile = useUserProfile(userId);
  return <ProfileImage uri={profile?.profileImage || fallbackUri} style={style} />;
};

async function resolveVibeSourceTitle(request: VibeRequest): Promise<string> {
  if (request.sourceTitle) return request.sourceTitle;
  if (request.sharedChatKind === 'profile') return 'Tonyte profile';
  const chatCollection = request.sharedChatKind === 'plan'
    ? FIRESTORE_COLLECTIONS.planChats
    : FIRESTORE_COLLECTIONS.eventChats;
  try {
    const chatSnap = await getDoc(doc(firestore, chatCollection, request.sharedChatId));
    if (chatSnap.exists()) {
      const data = chatSnap.data();
      const title = request.sharedChatKind === 'plan'
        ? String(data.planTitle || data.name || '')
        : String(data.eventTitle || data.name || '');
      if (title.trim()) return title.trim();
    }
  } catch {
    // Fall through to activity lookup.
  }
  try {
    const sourceCollection = request.sharedChatKind === 'plan'
      ? FIRESTORE_COLLECTIONS.plans
      : FIRESTORE_COLLECTIONS.activities;
    const activitySnap = await getDoc(doc(firestore, sourceCollection, request.sharedChatId));
    if (activitySnap.exists()) {
      const title = String(activitySnap.data().title || '');
      if (title.trim()) return title.trim();
    }
  } catch {
    // Optional label only.
  }
  return '';
}

// ─── Bottom-sheet animation hook ────────────────────────────────────────────

function useSheetAnim() {
  const anim = useRef(new Animated.Value(0)).current;

  const open = useCallback((onDone?: () => void) => {
    Animated.spring(anim, { toValue: 1, useNativeDriver: true, damping: 24, stiffness: 220 }).start(onDone);
  }, [anim]);

  const close = useCallback((onDone?: () => void) => {
    Animated.timing(anim, { toValue: 0, duration: 220, useNativeDriver: true }).start(onDone);
  }, [anim]);

  return { anim, open, close };
}

// ─── Backdrop overlay ────────────────────────────────────────────────────────

const Backdrop: React.FC<{ onPress?: () => void; dark?: boolean }> = ({ onPress, dark }) => (
  <TouchableWithoutFeedback onPress={onPress}>
    <View style={[StyleSheet.absoluteFill, dark ? s.backdropDark : s.backdropDim]} />
  </TouchableWithoutFeedback>
);

function buildParticipantSnapshot(userId: string, profile: PublicUserProfile) {
  return {
    userId,
    displayName: getUserDisplayName(profile),
    photoURL: profile.profileImage,
    username: profile.username,
    isVibeVerified: profile.faceVerified === true,
    isStudentVerified: profile.isStudentVerified === true,
    joinedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

// ─── Vibe Request Inbox Card ─────────────────────────────────────────────────

interface VibeRequestCardProps {
  request: VibeRequest;
  currentUserId: string;
  mode: 'received' | 'sent';
  onAccepted: (chatId: string, otherUserId: string, otherName: string) => void;
  onDeclined: () => void;
  onCancelled?: () => void;
  onOpenProfile: (userId: string, name?: string) => void;
}

const VibeRequestCard: React.FC<VibeRequestCardProps> = ({ request, currentUserId, mode, onAccepted, onDeclined, onCancelled, onOpenProfile }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const listThemeStyles = useMemo(() => createChatListThemeStyles(theme), [theme]);
  const [profile, setProfile] = useState<PublicUserProfile | null>(null);
  const [sourceTitle, setSourceTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const otherUserId = mode === 'received' ? request.fromUserId : request.toUserId;

  useEffect(() => {
    let cancelled = false;
    getUserProfile(otherUserId).then(p => { if (!cancelled) setProfile(p); });
    return () => { cancelled = true; };
  }, [otherUserId]);

  useEffect(() => {
    let cancelled = false;
    resolveVibeSourceTitle(request).then(title => { if (!cancelled) setSourceTitle(title); });
    return () => { cancelled = true; };
  }, [request]);

  const handleAccept = useCallback(async () => {
    if (busyRef.current || !currentUserId) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const chatId = await acceptVibeRequest({
        requestId: request.id,
        fromUserId: request.fromUserId,
        toUserId: request.toUserId,
      });
      setPreviewOpen(false);
      onAccepted(chatId, request.fromUserId, profile ? getUserDisplayName(profile) : t('chat.someone'));
    } catch (err) {
      console.warn('[Tonyte][RN][Vibe] accept failed', err);
      showMessage({ message: t('chat.requestAcceptFailed'), type: 'danger' });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [currentUserId, request, profile, onAccepted, t]);

  const handleDecline = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await declineVibeRequest(request.id);
      setPreviewOpen(false);
      onDeclined();
    } catch (err) {
      console.warn('[Tonyte][RN][Vibe] decline failed', err);
      showMessage({ message: t('chat.requestDeclineFailed'), type: 'danger' });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [request.id, onDeclined, t]);

  const handleCancel = useCallback(async () => {
    if (busyRef.current || request.status === 'accepted') return;
    busyRef.current = true;
    setBusy(true);
    try {
      await cancelVibeRequest(request.id);
      setPreviewOpen(false);
      onCancelled?.();
    } catch (err) {
      console.warn('[Tonyte][RN][Vibe] cancel failed', err);
      showMessage({ message: t('chat.requestCancelFailed'), type: 'danger' });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [onCancelled, request.id, request.status, t]);

  const displayName = profile ? getUserDisplayName(profile) : '...';
  const avatarUri = profile?.profileImage;
  const statusLabel = request.status === 'accepted' ? t('chat.accepted') : request.status === 'declined' ? t('chat.passed') : t('chat.pending');
  const timeLabel = formatRelativeTime(request.updatedAt || request.createdAt);
  const sourceLabel = sourceTitle || (
    request.sharedChatKind === 'profile'
      ? t('chat.profileSource')
      : request.sharedChatKind === 'plan'
        ? t('chat.sharedPlan')
        : t('chat.sharedEvent')
  );
  const sourceIcon = request.sharedChatKind === 'profile'
    ? 'user'
    : request.sharedChatKind === 'plan'
      ? 'calendar'
      : 'map-pin';
  const messagePreview = request.message?.trim() || t('chat.noRequestMessage');
  const interests = [...new Set((profile?.interests ?? []).map(i => i.trim()).filter(Boolean))].slice(0, 6);

  return (
    <>
    <Pressable
      onPress={() => setPreviewOpen(true)}
      android_ripple={{ color: 'rgba(38,208,206,0.14)' }}
      style={({ pressed }) => [s.vibeReqCard, listThemeStyles.vibeRequestCard, pressed && s.vibeReqCardPressed]}
    >
      <View style={s.vibeReqAccent} />
      <View style={s.vibeReqMainRow}>
        <View style={s.vibeReqAvatarWrap}>
          <ProfileImage uri={avatarUri} style={s.vibeReqAvatar} />
          <VibeVerifiedBadge verified={profile?.faceVerified === true} size={50} />
        </View>
        <View style={s.vibeReqContent}>
        <View style={s.vibeReqNameRow}>
          <Text style={[s.vibeReqName, listThemeStyles.textPrimary]} numberOfLines={1}>{displayName}</Text>
          {profile?.faceVerified && (
            <View style={s.vibeReqBadge}>
              <Icon name="user-check" size={9} color={Colors.turquoise} />
            </View>
          )}
          {profile?.isStudentVerified && (
            <View style={s.vibeReqBadge}>
              <Icon name="check-circle" size={9} color={Colors.sunset} />
            </View>
          )}
          {Boolean(timeLabel) && <Text style={[s.vibeReqTime, listThemeStyles.textMuted]}>{timeLabel}</Text>}
        </View>
        <Text style={[s.vibeReqLabel, listThemeStyles.vibeReqLabel]} numberOfLines={2}>
          {mode === 'received' ? t('chat.wantsToVibe') : t('chat.status').replace('{status}', statusLabel)}
        </Text>
        <Text style={[s.vibeReqMessage, listThemeStyles.vibeRequestMessage]} numberOfLines={2}>{messagePreview}</Text>
          <View style={[s.vibeSourcePill, listThemeStyles.vibeSourcePill]}>
            <Icon name={sourceIcon} size={10} color={theme.isDark ? 'rgba(224,242,254,0.82)' : theme.colors.accentSecondary} />
            <Text style={[s.vibeSourceText, listThemeStyles.vibeSourceText]} numberOfLines={1}>{sourceLabel}</Text>
          </View>
        </View>
      </View>
      <View style={s.vibeReqActions}>
        {mode === 'received' ? (
          <>
            <TouchableOpacity
              style={[s.vibeAcceptBtn, listThemeStyles.vibeAcceptBtn, busy && s.vibeActionDisabled]}
              onPress={handleAccept}
              disabled={busy}
              activeOpacity={0.8}
            >
              <Text style={[s.vibeAcceptText, listThemeStyles.vibeAcceptText]}>{t('chat.vibe')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.vibeDeclineBtn, listThemeStyles.vibeDeclineBtn, busy && s.vibeActionDisabled]}
              onPress={handleDecline}
              disabled={busy}
              activeOpacity={0.8}
            >
              <Text style={[s.vibeDeclineText, listThemeStyles.vibeDeclineText]}>{t('chat.pass')}</Text>
            </TouchableOpacity>
          </>
        ) : request.status === 'accepted' ? (
          <View style={[s.alreadyVibingPill, listThemeStyles.alreadyVibingPill]}>
            <Text style={[s.alreadyVibingText, listThemeStyles.alreadyVibingText]}>{t('chat.alreadyVibing')}</Text>
          </View>
        ) : (
          <TouchableOpacity
            style={[
              s.vibeDeclineBtn,
              listThemeStyles.vibeDeclineBtn,
              !theme.isDark && s.vibeCancelBtnDaylight,
              busy && s.vibeActionDisabled,
            ]}
            onPress={handleCancel}
            disabled={busy}
            activeOpacity={0.8}
          >
            <Text style={[
              s.vibeDeclineText,
              listThemeStyles.vibeDeclineText,
              !theme.isDark && s.vibeCancelTextDaylight,
            ]}>{request.status === 'pending' ? t('chat.cancel') : t('chat.delete')}</Text>
          </TouchableOpacity>
        )}
      </View>
    </Pressable>

    <Modal transparent visible={previewOpen} animationType="fade" onRequestClose={() => setPreviewOpen(false)}>
      <View style={s.vibePreviewRoot}>
        <Backdrop dark onPress={() => setPreviewOpen(false)} />
        <View style={s.vibePreviewSheetWrap}>
          <View style={s.vibePreviewSheet}>
            <View style={s.sheetHandle} />
            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={s.vibePreviewScroll}
            >
              <View style={s.vibePreviewHeader}>
                <View style={s.vibePreviewAvatarWrap}>
                  <ProfileImage uri={avatarUri} style={s.vibePreviewAvatar} />
                  <VibeVerifiedBadge verified={profile?.faceVerified === true} size={76} />
                </View>
                <View style={s.vibePreviewTitleBlock}>
                  <View style={s.vibePreviewNameRow}>
                    <Text style={s.vibePreviewName} numberOfLines={2}>{displayName}</Text>
                    {profile?.faceVerified ? (
                      <View style={s.vibePreviewBadge}>
                        <Icon name="user-check" size={12} color={Colors.turquoise} />
                        <Text style={s.vibePreviewBadgeText}>{t('chat.vibeVerified')}</Text>
                      </View>
                    ) : null}
                  </View>
                  {profile?.username ? <Text style={s.vibePreviewMeta}>@{profile.username}</Text> : null}
                  {profile?.location ? <Text style={s.vibePreviewMeta} numberOfLines={1}>{profile.location}</Text> : null}
                </View>
              </View>

              {profile?.bio ? (
                <View style={s.vibePreviewSection}>
                  <Text style={s.vibePreviewSectionLabel}>{t('chat.bio')}</Text>
                  <Text style={s.vibePreviewBody}>{profile.bio}</Text>
                </View>
              ) : null}

              {profile?.socialVibe ? (
                <View style={s.vibePreviewSection}>
                  <Text style={s.vibePreviewSectionLabel}>{t('chat.vibe')}</Text>
                  <Text style={s.vibePreviewBody}>{profile.socialVibe}</Text>
                </View>
              ) : null}

              {interests.length > 0 ? (
                <View style={s.vibePreviewSection}>
                  <Text style={s.vibePreviewSectionLabel}>{t('chat.interests')}</Text>
                  <View style={s.vibePreviewChips}>
                    {interests.map((interest, idx) => (
                      <View key={`${interest}-${idx}`} style={s.vibePreviewChip}>
                        <Text style={s.vibePreviewChipText}>{interest}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              ) : null}

              <View style={s.vibePreviewSection}>
                <Text style={s.vibePreviewSectionLabel}>{t('chat.requestMessage')}</Text>
                <Text style={s.vibePreviewMessage}>{messagePreview}</Text>
              </View>

              <View style={s.vibePreviewSource}>
                <Icon name={sourceIcon} size={14} color={Colors.turquoise} />
                <Text style={s.vibePreviewSourceText} numberOfLines={2}>{sourceLabel}</Text>
              </View>

              <TouchableOpacity
                activeOpacity={0.84}
                style={s.vibePreviewProfileBtn}
                onPress={() => {
                  setPreviewOpen(false);
                  onOpenProfile(otherUserId, displayName);
                }}
              >
                <Icon name="user" size={14} color="rgba(255,255,255,0.8)" />
                <Text style={s.vibePreviewProfileText}>{t('chat.viewProfile')}</Text>
              </TouchableOpacity>
            </ScrollView>

            <View style={s.vibePreviewActions}>
              {mode === 'received' ? (
                <>
                  <TouchableOpacity
                    style={[s.vibePreviewPassBtn, busy && s.vibeActionDisabled]}
                    onPress={handleDecline}
                    disabled={busy}
                    activeOpacity={0.82}
                  >
                    <Text style={s.vibePreviewPassText}>{t('chat.pass')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[s.vibePreviewVibeBtn, busy && s.vibeActionDisabled]}
                    onPress={handleAccept}
                    disabled={busy}
                    activeOpacity={0.82}
                  >
                    <Text style={s.vibePreviewVibeText}>{busy ? t('chat.working') : t('chat.vibe')}</Text>
                  </TouchableOpacity>
                </>
              ) : request.status === 'accepted' ? (
                <View style={s.vibePreviewAcceptedPill}>
                  <Text style={s.vibePreviewVibeText}>{t('chat.alreadyVibing')}</Text>
                </View>
              ) : (
                <TouchableOpacity
                  style={[s.vibePreviewPassBtn, s.vibePreviewFullBtn, listThemeStyles.vibePreviewCancelBtn, busy && s.vibeActionDisabled]}
                  onPress={handleCancel}
                  disabled={busy}
                  activeOpacity={0.82}
                >
                  <Text style={[s.vibePreviewPassText, listThemeStyles.vibePreviewCancelText]}>{request.status === 'pending' ? t('chat.cancelRequest') : t('chat.delete')}</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </View>
    </Modal>
    </>
  );
};

// ─── People Here Sheet ────────────────────────────────────────────────────────

type VibeStatus = 'none' | 'pending_sent' | 'pending_received' | 'connected' | 'self' | 'unavailable';

interface ParticipantVibeEntry {
  id: string;
  profile: PublicUserProfile;
  vibeStatus: VibeStatus;
  // Partner accounts open their partner profile and never get a personal Vibe action.
  isPartner?: boolean;
  partnerVenue?: VenueSummary | null;
  communityStatus: 'active' | 'left' | 'removed' | 'eligible' | undefined;
  communityRole: PartnerCommunityRole | undefined;
}

interface PeopleHereSheetProps {
  visible: boolean;
  chatId: string;
  chatKind: 'event' | 'plan' | 'community';
  participantIds: string[];
  currentUserId: string;
  onClose: () => void;
  onVibeAccepted: (chatId: string, otherUserId: string, otherName: string) => void;
}

const PeopleHereSheet: React.FC<PeopleHereSheetProps> = ({
  visible, chatId, chatKind, participantIds, currentUserId, onClose, onVibeAccepted,
}) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const { anim, open, close } = useSheetAnim();
  const [entries, setEntries] = useState<ParticipantVibeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  const [messageTarget, setMessageTarget] = useState<ParticipantVibeEntry | null>(null);
  const [vibeMessage, setVibeMessage] = useState('');
  // Plan admin/creator removal — Plan-only (not Events). isAdmin gates
  // whether the "Remove from Plan" action renders at all for this viewer;
  // creatorId additionally hides it on the creator's own row (the Cloud
  // Function also refuses to remove the creator server-side — this is only
  // about not offering a control that would always fail).
  const [planAdminInfo, setPlanAdminInfo] = useState<{ isAdmin: boolean; isOwner: boolean; creatorId: string | null } | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const participantsCollPath = chatKind === 'community'
    ? FIRESTORE_COLLECTIONS.partnerCommunityChats
    : chatKind === 'plan'
    ? FIRESTORE_COLLECTIONS.planChats
    : FIRESTORE_COLLECTIONS.eventChats;

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    let canManageCommunity = false;
    if (chatKind === 'plan' || chatKind === 'community') {
      try {
        const adminSnap = await getDoc(doc(firestore, chatKind === 'community' ? FIRESTORE_COLLECTIONS.partnerCommunityChats : FIRESTORE_COLLECTIONS.plans, chatId));
        if (adminSnap.exists()) {
          const d = adminSnap.data();
          let isCommunityAdmin = false;
          if (chatKind === 'community') {
            const ownMembership = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.partnerCommunityChats, chatId, 'members', currentUserId));
            isCommunityAdmin = d.ownerUid === currentUserId || (ownMembership.exists() && ownMembership.data().status === 'active' && ownMembership.data().role === 'co-admin');
            canManageCommunity = isCommunityAdmin;
          }
          const adminIds: string[] = chatKind === 'community' ? [d.ownerUid].filter(Boolean) : Array.isArray(d.adminIds) ? d.adminIds : [d.hostId, d.createdBy].filter(Boolean);
          setPlanAdminInfo({
            isAdmin: chatKind === 'community' ? isCommunityAdmin : adminIds.includes(currentUserId),
            isOwner: chatKind === 'community' && d.ownerUid === currentUserId,
            creatorId: chatKind === 'community' ? d.ownerUid : typeof d.createdBy === 'string' ? d.createdBy : (typeof d.hostId === 'string' ? d.hostId : null),
          });
        } else {
          setPlanAdminInfo(null);
        }
      } catch (err) {
        console.warn('[Tonyte][RN][PeopleHere] plan admin lookup failed', { chatId, err });
        setPlanAdminInfo(null);
      }
    }

    // Upsert own profile snapshot so other members can see us in the list.
    // Non-fatal — don't block the load if this fails (e.g. first join).
    if (chatKind !== 'community') {
      try {
        const myProfile = await getUserProfile(currentUserId);
        await setDoc(
          doc(firestore, participantsCollPath, chatId, 'participants', currentUserId),
          buildParticipantSnapshot(currentUserId, myProfile),
          { merge: true },
        );
      } catch (err) {
        console.warn('[Tonyte][RN][PeopleHere] own snapshot upsert failed', { chatId, err });
      }
    }

    // Load participant snapshot docs (written by each user when they open People Here).
    // Fall back to getUserProfile for any uid that hasn't written a snapshot yet.
    let snapshotMap: Record<string, PublicUserProfile> = {};
    try {
      const snapDocs = await getDocs(
        collection(firestore, participantsCollPath, chatId, chatKind === 'community' ? 'members' : 'participants'),
      );
      snapDocs.forEach(d => {
        const data = d.data();
        if (chatKind === 'community' && data.status !== 'active') return;
        if (chatKind !== 'community') snapshotMap[d.id] = {
          id: d.id,
          displayName: String(data.displayName ?? ''),
          username: String(data.username ?? ''),
          profileImage: String(data.photoURL ?? ''),
          coverPhoto: '',
          city: '',
          location: '',
          bio: publicBio(data.bio),
          interests: Array.isArray(data.interests) ? data.interests : [],
          eventsCount: 0,
          plansCount: 0,
          faceVerified: data.isVibeVerified === true || data.faceVerified === true,
          isStudentVerified: data.isStudentVerified === true,
        };
      });
    } catch (err) {
      console.warn('[Tonyte][RN][PeopleHere] participants snapshot load failed', {
        path: `${participantsCollPath}/${chatId}/participants`,
        err,
      });
      // snapshotMap stays empty — we'll fall back to getUserProfile per-uid below
    }

    // Snapshot documents cache public profile data; they do not define who
    // belongs to the Plan/event.
    let communityStatuses = new Map<string, ParticipantVibeEntry['communityStatus']>();
    let communityRoles = new Map<string, PartnerCommunityRole>();
    let resolvedParticipantIds = [...new Set(participantIds)];
    if (chatKind === 'community' && canManageCommunity) {
      try {
        const managedMembers = await listPartnerCommunityMembers(chatId);
        resolvedParticipantIds = [...new Set(managedMembers.map(member => member.userId))];
        communityStatuses = new Map(managedMembers.map(member => [member.userId, member.status]));
        communityRoles = new Map(managedMembers.map(member => [member.userId, member.role]));
      } catch (err) {
        console.warn('[Tonyte][RN][Community] moderation member list failed', { chatId, err });
      }
    }

    try {
      const results = await Promise.all(
        resolvedParticipantIds.map(async (uid): Promise<ParticipantVibeEntry | null> => {
          // Profile: prefer snapshot, fall back to users collection
          let profile: PublicUserProfile;
          if (snapshotMap[uid]) {
            profile = snapshotMap[uid];
          } else {
            try {
              profile = await getUserProfile(uid);
            } catch (err) {
              console.warn('[Tonyte][RN][PeopleHere] profile load failed, skipping', { uid, err });
              return null; // skip unreadable participant
            }
          }

          // Partner identity comes from users.role + venues.ownerUid, never
          // the display name. If it can't be checked, offer no personal Vibe.
          const identity = await resolvePartnerIdentity(uid).catch((err: unknown) => {
            console.warn('[Tonyte][RN][PeopleHere] partner identity check failed', { uid, err });
            return null;
          });
          const isPartnerRow = identity?.kind === 'partner';
          const publicVenues = visiblePartnerVenues(identity, false);
          const partnerVenue = isPartnerRow && publicVenues.length === 1 ? publicVenues[0] : null;
          const base = { id: uid, profile, isPartner: isPartnerRow, partnerVenue, communityStatus: communityStatuses.get(uid), communityRole: communityRoles.get(uid) };

          // Vibe/blocked state errors are surfaced and make this row
          // unavailable; a rules denial must never look like "no relationship".
          let vibeStatus: VibeStatus = uid === currentUserId ? 'self' : 'none';
          if (uid === currentUserId) return { ...base, vibeStatus };
          if (communityStatuses.get(uid) === 'removed') return { ...base, vibeStatus: 'unavailable' as const, communityStatus: 'removed' as const };
          if (isPartnerRow) return { ...base, vibeStatus: 'none' as const };
          if (!identity) return { ...base, vibeStatus: 'unavailable' as const };
          try {
            const pairKey = getVibePairKey(currentUserId, uid);
            const [relationshipSnap, sentSnap, receivedSnap, outgoingBlock, incomingBlock] = await Promise.all([
              getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRelationships, pairKey)),
              getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, `${currentUserId}_${uid}`)),
              getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, `${uid}_${currentUserId}`)),
              getDoc(doc(firestore, FIRESTORE_COLLECTIONS.blocks, `${currentUserId}_${uid}`)),
              getDoc(doc(firestore, FIRESTORE_COLLECTIONS.blocks, `${uid}_${currentUserId}`)),
            ]);
            if (outgoingBlock?.exists() || incomingBlock?.exists()) {
              vibeStatus = 'unavailable';
            } else if (relationshipSnap?.exists() && relationshipSnap.data().status === 'active') {
              vibeStatus = 'connected';
            } else if (sentSnap?.exists()) {
              const st = sentSnap.data().status;
              vibeStatus = st === 'accepted' ? 'connected' : st === 'pending' ? 'pending_sent' : 'none';
            } else if (receivedSnap?.exists()) {
              const st = receivedSnap.data().status;
              vibeStatus = st === 'accepted' ? 'connected' : st === 'pending' ? 'pending_received' : 'none';
            }
          } catch (err) {
            console.warn('[Tonyte][RN][PeopleHere] vibe status check failed', { uid, err });
            vibeStatus = 'unavailable';
          }

          return { ...base, vibeStatus };
        }),
      );

      const valid = results.filter((r): r is ParticipantVibeEntry => r !== null);
      setEntries(valid);
      if (valid.length === 0 && resolvedParticipantIds.length > 0) {
        setLoadError(t('chat.travelersLoadFailed'));
      }
    } catch (err) {
      console.warn('[Tonyte][RN][PeopleHere] loadEntries failed', { chatId, err });
      setLoadError(t('chat.travelersLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [participantIds, currentUserId, chatId, chatKind, participantsCollPath, t]);

  useEffect(() => {
    if (!visible) return;
    open();
    void loadEntries();
  }, [visible, open, loadEntries]);

  const handleClose = useCallback(() => {
    close(() => onClose());
  }, [close, onClose]);

  const openEntryProfile = useCallback((entry: ParticipantVibeEntry) => {
    if (entry.partnerVenue) {
      navigation.navigate('VenueProfile', { venueId: entry.partnerVenue.id, venue: entry.partnerVenue });
      return;
    }
    // PublicProfile routes partner accounts to their partner profile too.
    navigation.navigate('PublicProfile', { userId: entry.id });
  }, [navigation]);

  const handleRemoveMember = useCallback((entry: ParticipantVibeEntry) => {
    const name = getUserDisplayName(entry.profile) || t('chat.thisUser');
    Alert.alert(
      t('chat.removeFromPlanQuestion').replace('{name}', name),
      t('chat.removeFromPlanMessage'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.remove'),
          style: 'destructive',
          onPress: async () => {
            setRemovingId(entry.id);
            try {
              if (chatKind === 'community') await removePartnerCommunityMember(chatId, entry.id);
              else await removePlanMember(chatId, entry.id);
              setEntries(prev => prev.filter(e => e.id !== entry.id));
              showMessage({ message: t('chat.memberRemoved'), type: 'success' });
            } catch (err) {
              console.warn('[Tonyte][RN][PeopleHere] removePlanMember failed', { chatId, targetUserId: entry.id, err });
              Alert.alert(t('chat.couldNotRemoveMember'), t('common.tryAgain'));
            } finally {
              setRemovingId(null);
            }
          },
        },
      ],
    );
  }, [chatId, chatKind, t]);

  const handleReinstateMember = useCallback(async (entry: ParticipantVibeEntry) => {
    setRemovingId(entry.id);
    try {
      const result = await reinstatePartnerCommunityMember(chatId, entry.id);
      setEntries(prev => prev.map(item => item.id === entry.id ? { ...item, communityStatus: result.rejoined ? 'active' : 'eligible' } : item));
      showMessage({ message: result.rejoined ? t('partnerCommunity.rejoined') : t('partnerCommunity.allowedBack'), type: 'success' });
    } catch {
      Alert.alert(t('common.error'), t('common.tryAgain'));
    } finally {
      setRemovingId(null);
    }
  }, [chatId, t]);

  const handleCommunityMemberActions = useCallback((entry: ParticipantVibeEntry) => {
    const name = getUserDisplayName(entry.profile) || t('chat.thisUser');
    const buttons: any[] = [];
    if (planAdminInfo?.isOwner) {
      const isCoAdmin = entry.communityRole === 'co-admin';
      buttons.push({
        text: t(isCoAdmin ? 'partnerCommunity.removeCoAdmin' : 'partnerCommunity.makeCoAdmin'),
        onPress: async () => {
          setRemovingId(entry.id);
          try {
            const result = await setPartnerCommunityCoAdmin(chatId, entry.id, !isCoAdmin);
            setEntries(prev => prev.map(item => item.id === entry.id ? { ...item, communityRole: result.role } : item));
            showMessage({ message: t(isCoAdmin ? 'partnerCommunity.coAdminRemoved' : 'partnerCommunity.coAdminAdded').replace('{name}', name), type: 'success' });
          } catch {
            Alert.alert(t('common.error'), t('common.tryAgain'));
          } finally {
            setRemovingId(null);
          }
        },
      });
    }
    if (entry.communityRole !== 'co-admin' || planAdminInfo?.isOwner) {
      buttons.push({ text: t('chat.removeFromPlan'), style: 'destructive', onPress: () => handleRemoveMember(entry) });
    }
    buttons.push({ text: t('common.cancel'), style: 'cancel' });
    Alert.alert(name, undefined, buttons);
  }, [chatId, handleRemoveMember, planAdminInfo?.isOwner, t]);

  const openVibeMessagePrompt = useCallback((entry: ParticipantVibeEntry) => {
    setMessageTarget(entry);
    setVibeMessage('');
  }, []);

  const closeVibeMessagePrompt = useCallback(() => {
    if (sending) return;
    setMessageTarget(null);
    setVibeMessage('');
  }, [sending]);

  const handleSendRequest = useCallback(async (toUserId: string, message?: string) => {
    if (!auth.currentUser) {
      showMessage({ message: t('chat.signInSendVibes'), type: 'warning' });
      return;
    }
    if (!currentUserId || sending) return;
    setSending(toUserId);
    try {
      // Ensure the sender has a participant snapshot so the Firestore rule can verify
      // they are in the event/plan chat before writing the vibe request document.
      if (chatKind !== 'community') {
        try {
          const myProfile = await getUserProfile(currentUserId);
          await setDoc(
            doc(firestore, participantsCollPath, chatId, 'participants', currentUserId),
            buildParticipantSnapshot(currentUserId, myProfile),
            { merge: true },
          );
        } catch (participantErr) {
          console.warn('[Tonyte][RN][Vibe] participant pre-upsert failed', {
            path: `${participantsCollPath}/${chatId}/participants/${currentUserId}`,
            err: participantErr,
          });
          // Non-fatal — proceed; the rule also accepts isInterestedInEvent.
        }
      }

      const requestPath = `vibeRequests/${currentUserId}_${toUserId}`;
      console.info('[Tonyte][RN][Vibe] sending request', { path: requestPath, sharedChatId: chatId, sharedChatKind: chatKind });
      await sendVibeRequest({ fromUserId: currentUserId, toUserId, sharedChatId: chatId, sharedChatKind: chatKind === 'community' ? 'profile' : chatKind, message });
      setEntries(prev => prev.map(e => e.id === toUserId ? { ...e, vibeStatus: 'pending_sent' } : e));
      setMessageTarget(null);
      setVibeMessage('');
    } catch (err) {
      console.warn('[Tonyte][RN][Vibe] send failed', {
        path: `vibeRequests/${currentUserId}_${toUserId}`,
        err,
      });
      showMessage({ message: t('chat.vibeRequestSendFailed'), type: 'danger' });
    } finally {
      setSending(null);
    }
  }, [currentUserId, chatId, chatKind, sending, participantsCollPath, t]);

  const submitVibeMessage = useCallback(() => {
    if (!messageTarget) return;
    void handleSendRequest(messageTarget.id, vibeMessage.trim());
  }, [handleSendRequest, messageTarget, vibeMessage]);

  const handleAcceptReceived = useCallback(async (entry: ParticipantVibeEntry) => {
    if (!currentUserId || sending) return;
    setSending(entry.id);
    const requestId = `${entry.id}_${currentUserId}`;
    try {
      const privateChatId = await acceptVibeRequest({
        requestId,
        fromUserId: entry.id,
        toUserId: currentUserId,
      });
      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, vibeStatus: 'connected' } : e));
      showMessage({ message: t('chat.nowVibingWith').replace('{name}', getUserDisplayName(entry.profile)), type: 'success' });
      onVibeAccepted(privateChatId, entry.id, getUserDisplayName(entry.profile));
    } catch (err) {
      console.warn('[Tonyte][RN][Vibe] accept from sheet failed', err);
      showMessage({ message: t('chat.requestAcceptFailed'), type: 'danger' });
    } finally {
      setSending(null);
    }
  }, [currentUserId, sending, onVibeAccepted, t]);

  const handleDeclineReceived = useCallback(async (entry: ParticipantVibeEntry) => {
    if (!currentUserId || sending) return;
    setSending(entry.id);
    try {
      await declineVibeRequest(`${entry.id}_${currentUserId}`);
      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, vibeStatus: 'none' } : e));
    } catch (err) {
      console.warn('[Tonyte][RN][Vibe] decline from member sheet failed', { userId: entry.id, err });
      showMessage({ message: t('chat.requestDeclineFailed'), type: 'danger' });
    } finally {
      setSending(null);
    }
  }, [currentUserId, sending, t]);

  if (!visible) return null;

  const slideStyle = {
    transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [800, 0] }) }],
    opacity: anim,
  };

  return (
    <Modal visible transparent animationType="none" onRequestClose={handleClose} statusBarTranslucent>
      <Animated.View style={[StyleSheet.absoluteFill, s.fullOverlay, { opacity: anim }]}>
        {/* The modal draws under the status bar (statusBarTranslucent), so start
            the sheet below it — otherwise its header hides behind the clock/notch. */}
        <Animated.View style={[s.peopleHereSheet, slideStyle, { marginTop: insets.top + 8, paddingBottom: insets.bottom + 16 }]}>
          {/* Header */}
          <View style={s.peopleHereHeader}>
            <View>
              <Text style={s.peopleHereLabel}>{t('chat.peopleHere')}</Text>
              <Text style={s.peopleHereTitle}>{t('chat.connectTravelers')}</Text>
            </View>
            <TouchableOpacity style={s.sheetCloseBtn} onPress={handleClose} activeOpacity={0.8}>
              <Icon name="x" size={18} color={Colors.textPrimary} />
            </TouchableOpacity>
          </View>

          {/* List */}
          {loading ? (
            <View style={s.peopleHereLoading}>
              <Text style={s.membersEmptyText}>{t('chat.loadingTravelers')}</Text>
            </View>
          ) : loadError ? (
            <View style={s.peopleHereLoading}>
              <View style={s.peopleHereErrorIcon}>
                <Icon name="alert-circle" size={22} color="rgba(255,255,255,0.35)" />
              </View>
              <Text style={s.peopleHereErrorText}>{loadError}</Text>
              <TouchableOpacity
                style={s.peopleHereRetryBtn}
                onPress={() => void loadEntries()}
                activeOpacity={0.8}
              >
                <Text style={s.peopleHereRetryText}>{t('chat.tryAgain')}</Text>
              </TouchableOpacity>
            </View>
          ) : entries.length === 0 ? (
            <View style={s.peopleHereLoading}>
              <View style={s.peopleHereErrorIcon}>
                <Icon name="users" size={22} color="rgba(255,255,255,0.35)" />
              </View>
              <Text style={s.membersEmptyText}>{t('chat.noTravelers')}</Text>
              <Text style={s.peopleHereEmptySub}>{t('chat.noTravelersSub')}</Text>
            </View>
          ) : (
            <FlatList
              data={entries}
              keyExtractor={item => item.id}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ gap: 10, paddingHorizontal: 16, paddingTop: 8, paddingBottom: 16 }}
              renderItem={({ item }) => {
                const name = item.partnerVenue?.name || getUserDisplayName(item.profile);
                const isBusy = sending === item.id;
                const partnerMeta = item.partnerVenue ? [item.partnerVenue.category, item.partnerVenue.city].filter(Boolean).join(' · ') : '';
                return (
                  <View style={s.peopleCard}>
                    <TouchableOpacity
                      style={s.peopleAvatarWrap}
                      onPress={() => openEntryProfile(item)}
                      activeOpacity={0.76}
                      accessibilityRole="button"
                      accessibilityLabel={name}
                    >
                      {item.partnerVenue?.logoUrl
                        ? <ProfileImage uri={item.partnerVenue.logoUrl} style={s.peopleAvatar} />
                        : <LiveProfileImage userId={item.id} fallbackUri={item.profile.profileImage} style={s.peopleAvatar} />}
                      {item.isPartner ? null : <VibeVerifiedBadge verified={item.profile.faceVerified === true} size={52} />}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={s.peopleInfo}
                      onPress={() => openEntryProfile(item)}
                      activeOpacity={0.76}
                      accessibilityRole="button"
                      accessibilityLabel={name}
                    >
                      <View style={s.peopleNameRow}>
                        <Text style={s.peopleName} numberOfLines={1}>{name}</Text>
                        {chatKind === 'community' && (item.communityRole === 'admin' || item.communityRole === 'co-admin') ? (
                          <View style={s.communityRoleBadge}>
                            <Text style={s.communityRoleText}>{t(item.communityRole === 'admin' ? 'partnerCommunity.admin' : 'partnerCommunity.coAdmin')}</Text>
                          </View>
                        ) : null}
                        {!item.isPartner && item.profile.faceVerified && (
                          <View style={s.peopleVerifiedBadge}>
                            <Icon name="user-check" size={9} color={Colors.turquoise} />
                          </View>
                        )}
                      </View>
                      {item.isPartner ? (
                        partnerMeta ? <Text style={s.peopleBio} numberOfLines={1}>{partnerMeta}</Text> : null
                      ) : Boolean(item.profile.bio) && (
                        <Text style={s.peopleBio} numberOfLines={2}>{item.profile.bio}</Text>
                      )}
                      {!item.isPartner && item.profile.interests.length > 0 && (
                        <View style={s.peopleTags}>
                          {[...new Set(item.profile.interests.map(i => i.trim()).filter(Boolean))].slice(0, 3).map((tag, idx) => (
                            <View key={`${tag}-${idx}`} style={s.peopleTag}>
                              <Text style={s.peopleTagText}>{tag}</Text>
                            </View>
                          ))}
                        </View>
                      )}
                    </TouchableOpacity>
                    <View style={s.peopleAction}>
                      {item.communityStatus === 'removed' ? (
                        <TouchableOpacity style={s.vibeBtn} onPress={() => void handleReinstateMember(item)} disabled={removingId === item.id}>
                          <Text style={s.vibeBtnText}>{t('partnerCommunity.allowBack')}</Text>
                        </TouchableOpacity>
                      ) : item.vibeStatus === 'self' ? null : item.isPartner ? (
                        <TouchableOpacity
                          style={s.vibeBtn}
                          onPress={() => openEntryProfile(item)}
                          activeOpacity={0.8}
                          accessibilityRole="button"
                        >
                          <Icon name="user" size={12} color={Colors.turquoise} />
                          <Text style={s.vibeBtnText}>{t('chat.viewProfile')}</Text>
                        </TouchableOpacity>
                      ) : item.vibeStatus === 'unavailable' ? null : item.vibeStatus === 'connected' ? (
                        <View style={s.vibingBadge}>
                          <Icon name="check" size={11} color={Colors.turquoise} />
                          <Text style={s.vibingText}>{t('chat.vibing')}</Text>
                        </View>
                      ) : item.vibeStatus === 'pending_sent' ? (
                        <View style={s.sentBadge}>
                          <Text style={s.sentText}>{t('chat.sent')}</Text>
                        </View>
                      ) : item.vibeStatus === 'pending_received' ? (
                        <View>
                          <TouchableOpacity
                            style={[s.vibeBtn, isBusy && { opacity: 0.5 }]}
                            onPress={() => handleAcceptReceived(item)}
                            disabled={isBusy}
                            activeOpacity={0.8}
                          >
                            <Icon name="zap" size={12} color={Colors.turquoise} />
                            <Text style={s.vibeBtnText}>{t('chat.accept')}</Text>
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => handleDeclineReceived(item)} disabled={isBusy} activeOpacity={0.8}>
                            <Text style={s.sentText}>{t('chat.pass')}</Text>
                          </TouchableOpacity>
                        </View>
                      ) : (
                        <TouchableOpacity
                          style={[s.vibeBtn, isBusy && { opacity: 0.5 }]}
                          onPress={() => openVibeMessagePrompt(item)}
                          disabled={isBusy}
                          activeOpacity={0.8}
                        >
                          <Icon name="zap" size={12} color={Colors.turquoise} />
                          <Text style={s.vibeBtnText}>{t('chat.vibe')}</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    {(chatKind === 'plan' || chatKind === 'community') && item.communityStatus !== 'removed' && planAdminInfo?.isAdmin && item.id !== currentUserId && item.id !== planAdminInfo.creatorId && (
                      <TouchableOpacity
                        style={s.peopleRemoveBtn}
                        onPress={() => chatKind === 'community' ? handleCommunityMemberActions(item) : handleRemoveMember(item)}
                        disabled={removingId === item.id}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel={t('chat.removeFromPlan')}
                      >
                        <Icon name="more-vertical" size={18} color="rgba(255,255,255,0.5)" />
                      </TouchableOpacity>
                    )}
                  </View>
                );
              }}
            />
          )}
        </Animated.View>

        {messageTarget ? (
          <View style={s.vibeMessageOverlay}>
            <TouchableWithoutFeedback onPress={closeVibeMessagePrompt}>
              <View style={StyleSheet.absoluteFill} />
            </TouchableWithoutFeedback>
            <KeyboardAvoidingView
              behavior={Platform.OS === 'ios' ? 'padding' : undefined}
              style={s.vibeMessageKeyboard}
            >
              <View style={s.vibeMessageDialog}>
                <View style={s.vibeMessageIcon}>
                  <Icon name="zap" size={18} color={Colors.turquoise} />
                </View>
                <Text style={s.vibeMessageTitle}>{t('chat.sendVibe')}</Text>
                <Text style={s.vibeMessageSub}>
                  {t('chat.sendVibeSub').replace('{name}', getUserDisplayName(messageTarget.profile))}
                </Text>
                <TextInput
                  style={s.vibeMessageInput}
                  value={vibeMessage}
                  onChangeText={setVibeMessage}
                  placeholder={t('chat.optionalMessage')}
                  placeholderTextColor="rgba(255,255,255,0.32)"
                  maxLength={140}
                  multiline
                  autoFocus
                />
                <Text style={s.vibeMessageCount}>{vibeMessage.length}/140</Text>
                <View style={s.vibeMessageActions}>
                  <TouchableOpacity
                    style={s.vibeMessageCancel}
                    onPress={closeVibeMessagePrompt}
                    disabled={Boolean(sending)}
                    activeOpacity={0.84}
                  >
                    <Text style={s.vibeMessageCancelText}>{t('chat.cancel')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[s.vibeMessageSend, sending === messageTarget.id && s.vibeMessageSendDisabled]}
                    onPress={submitVibeMessage}
                    disabled={sending === messageTarget.id}
                    activeOpacity={0.86}
                  >
                    <Text style={s.vibeMessageSendText}>{sending === messageTarget.id ? t('chat.sending') : t('chat.sendVibeAction')}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </KeyboardAvoidingView>
          </View>
        ) : null}
      </Animated.View>
    </Modal>
  );
};

// ─── Group Info Sheet ────────────────────────────────────────────────────────

interface MemberInfo { id: string; name: string; photo: string; faceVerified?: boolean }
interface GroupActivity { id: string; title: string; description: string; address: string; city: string; coverPhotoURL?: string; timeLabel: string }

interface GroupInfoSheetProps {
  visible: boolean;
  chatId: string;
  kind: ChatItem['kind'];
  chatName: string;
  chatPhoto: string;
  participantIds: string[];
  currentUserId: string;
  onClose: () => void;
  onDeleteChat: () => void;
  onToggleMute: () => void;
  isMuted: boolean;
  onLeaveSuccess: () => void;
  onPeopleHere: () => void;
  onDeletePlan?: () => void;
}

const GroupInfoSheet: React.FC<GroupInfoSheetProps> = ({
  visible, chatId, kind, chatName, chatPhoto, participantIds, currentUserId, onClose, onDeleteChat, onToggleMute, isMuted, onLeaveSuccess, onPeopleHere, onDeletePlan,
}) => {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const { anim, open, close } = useSheetAnim();
  const [activity, setActivity] = useState<GroupActivity | null>(null);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [leaving, setLeaving] = useState(false);
  const [leaveConfirmVisible, setLeaveConfirmVisible] = useState(false);
  const [adminIds, setAdminIds] = useState<string[]>([]);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const isPlan = kind === 'plan';
  const isCommunity = kind === 'community';
  const isAdmin = (isPlan || isCommunity) && adminIds.includes(currentUserId);

  useEffect(() => {
    if (!visible) return;
    open();
    let cancelled = false;
    setLoading(true);
    setActivity(null);
    setMembers([]);

    const loadData = async () => {
      // Load activity info
      try {
        const sourceCollection = kind === 'community'
          ? FIRESTORE_COLLECTIONS.partnerCommunityChats
          : kind === 'plan'
          ? FIRESTORE_COLLECTIONS.plans
          : FIRESTORE_COLLECTIONS.activities;
        const snap = await getDoc(doc(firestore, sourceCollection, chatId));
        if (!cancelled && snap.exists()) {
          const d = snap.data();
          const buildTime = () => {
            const parts: string[] = [];
            if (d.startDate) parts.push(d.startDate);
            if (d.startTime) parts.push(d.startTime);
            return parts.join(' · ');
          };
          setActivity({
            id: snap.id,
            title: d.title ?? d.name ?? chatName,
            description: d.description ?? '',
            address: d.address ?? d.locationName ?? d.location ?? '',
            city: d.city ?? '',
            coverPhotoURL: d.coverPhoto ?? d.coverImageUrl ?? d.coverImage ?? d.imageUrl ?? '',
            timeLabel: buildTime(),
          });
          if (kind === 'community') setDescriptionDraft(String(d.description ?? ''));
          if (!cancelled) {
            setAdminIds(kind === 'community' ? [d.ownerUid].filter(Boolean) : Array.isArray(d.adminIds) ? d.adminIds : [d.hostId, d.createdBy].filter(Boolean));
          }
        }
      } catch {
        // Activity may not exist — non-fatal
      }

      // Load member profiles from the canonical participant snapshot collection.
      try {
        let idsForProfiles = participantIds;
        if (kind === 'event') {
          const participantSnap = await getDocs(collection(firestore, FIRESTORE_COLLECTIONS.eventChats, chatId, 'participants'));
          idsForProfiles = participantSnap.docs.map(d => d.id);
        }
        const profileList = await Promise.all(
          idsForProfiles.map(uid =>
            getUserProfile(uid)
              .then(p => ({ id: uid, name: getUserDisplayName(p), photo: p.profileImage, faceVerified: p.faceVerified === true }))
              .catch(() => ({ id: uid, name: uid === currentUserId ? t('chat.you') : t('chat.traveler'), photo: '', faceVerified: false })),
          ),
        );
        if (!cancelled) {
          setMembers(profileList);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };

    void loadData();
    return () => { cancelled = true; };
  }, [visible, chatId, kind, participantIds, currentUserId, chatName, open, t]);

  const handleClose = useCallback(() => {
    close(() => onClose());
  }, [close, onClose]);

  const handleLeave = useCallback(async () => {
    setLeaving(true);
    const collectionName = isCommunity ? FIRESTORE_COLLECTIONS.partnerCommunityChats : isPlan ? FIRESTORE_COLLECTIONS.planChats : FIRESTORE_COLLECTIONS.eventChats;
    try {
      if (isCommunity) {
        await leavePartnerCommunity(chatId);
      } else if (isPlan) {
        const planRef = doc(firestore, FIRESTORE_COLLECTIONS.plans, chatId);
        const planSnap = await getDoc(planRef);
        const remainingIds = getPlanMemberIds(planSnap.exists() ? planSnap.data() : null)
          .filter(id => id !== currentUserId);
        await updateDoc(planRef, {
          participantIds: arrayRemove(currentUserId),
          members: arrayRemove(currentUserId),
          participants: remainingIds.length,
        });
        await updateDoc(doc(firestore, collectionName, chatId), {
          memberIds: arrayRemove(currentUserId),
          members: arrayRemove(currentUserId),
        });
        await deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, chatId, 'participants', currentUserId)).catch(error => {
          console.warn('[Tonyte][RN][PlanMembers] legacy participant snapshot delete failed', { chatId, currentUserId, error });
        });
      } else {
        await postEventChatMemberNotice(chatId, currentUserId, 'left');
        await updateDoc(doc(firestore, collectionName, chatId), { memberIds: arrayRemove(currentUserId) });
      }
      onLeaveSuccess();
    } catch (err) {
      console.warn('[Tonyte][RN][Chat] leave failed', { chatId, err });
      setLeaving(false);
      showMessage({ message: t('chat.leaveFailed'), type: 'danger' });
    }
  }, [isCommunity, isPlan, chatId, currentUserId, onLeaveSuccess, t]);

  const handleSaveDescription = useCallback(async () => {
    if (savingDescription) return;
    setSavingDescription(true);
    try {
      const result = await updatePartnerCommunityDescription(chatId, descriptionDraft);
      setActivity(prev => prev ? { ...prev, description: result.description } : prev);
      setDescriptionDraft(result.description);
      setEditingDescription(false);
      showMessage({ message: t('partnerCommunity.descriptionSaved'), type: 'success' });
    } catch {
      Alert.alert(t('common.error'), t('partnerCommunity.descriptionSaveFailed'));
    } finally {
      setSavingDescription(false);
    }
  }, [chatId, descriptionDraft, savingDescription, t]);

  if (!visible) return null;

  const heroSrc = activity?.coverPhotoURL || chatPhoto || DEFAULT_COVER;
  const title = activity?.title || chatName;
  const venue = activity?.address || activity?.city || '';
  const description = activity?.description || '';
  const memberCount = members.length;

  const slideStyle = {
    transform: [{
      translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [800, 0] }),
    }],
    opacity: anim,
  };

  return (
    <Modal visible transparent animationType="none" onRequestClose={handleClose} statusBarTranslucent>
      <Animated.View style={[StyleSheet.absoluteFill, s.fullOverlay, { opacity: anim }]}>
        <Animated.ScrollView
          style={[s.groupInfoScroll, slideStyle]}
          contentContainerStyle={{ paddingBottom: insets.bottom + 120 }}
          showsVerticalScrollIndicator={false}
        >
          {/* Hero */}
          <View style={s.heroWrap}>
            <Image source={{ uri: heroSrc }} style={s.heroImage} />
            <LinearGradient
              colors={['transparent', 'rgba(2,5,9,0.55)', '#020509']}
              locations={[0, 0.5, 1]}
              style={StyleSheet.absoluteFill}
            />
            <TouchableOpacity
              onPress={handleClose}
              style={[s.heroCloseBtn, { top: insets.top + 16 }]}
              activeOpacity={0.8}
            >
              <Icon name="x" size={18} color="#fff" />
            </TouchableOpacity>
          </View>

          <View style={s.groupInfoContent}>
            {/* Header card */}
            <View style={s.infoCard}>
              <View style={[s.typeBadge, (isPlan || isCommunity) ? s.typeBadgePlan : s.typeBadgeEvent]}>
                <Text style={[s.typeBadgeText, { color: (isPlan || isCommunity) ? Colors.turquoise : Colors.sunset }]}>
                  {isCommunity ? t('partnerCommunity.button') : isPlan ? t('chat.planChat') : t('chat.eventChat')}
                </Text>
              </View>
              <Text style={s.infoTitle}>{title}</Text>
              {Boolean(activity?.timeLabel) && (
                <View style={s.infoRow}>
                  <Icon name="calendar" size={13} color={Colors.turquoise} />
                  <Text style={s.infoRowText}>{activity!.timeLabel}</Text>
                </View>
              )}
              {Boolean(venue) && (
                <View style={s.infoRow}>
                  <Icon name="map-pin" size={13} color={Colors.sunset} />
                  <Text style={[s.infoRowText, { color: 'rgba(255,255,255,0.45)' }]}>{venue}</Text>
                </View>
              )}
              {isCommunity && isAdmin && editingDescription ? (
                <View style={s.communityDescriptionEditor}>
                  <TextInput
                    value={descriptionDraft}
                    onChangeText={setDescriptionDraft}
                    placeholder={t('partnerCommunity.descriptionPlaceholder')}
                    placeholderTextColor="rgba(255,255,255,0.35)"
                    style={s.communityDescriptionInput}
                    multiline
                    maxLength={500}
                    autoFocus
                  />
                  <Text style={s.communityDescriptionCount}>{descriptionDraft.length}/500</Text>
                  <View style={s.communityDescriptionActions}>
                    <TouchableOpacity onPress={() => { setDescriptionDraft(description); setEditingDescription(false); }} disabled={savingDescription}>
                      <Text style={s.communityDescriptionCancel}>{t('common.cancel')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={s.communityDescriptionSave} onPress={() => void handleSaveDescription()} disabled={savingDescription}>
                      {savingDescription ? <ActivityIndicator size="small" color="#031014" /> : <Text style={s.communityDescriptionSaveText}>{t('common.save')}</Text>}
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <TouchableOpacity
                  disabled={!isCommunity || !isAdmin}
                  onPress={() => setEditingDescription(true)}
                  style={s.communityDescriptionDisplay}
                  activeOpacity={0.75}
                >
                  <Text style={[s.infoDescription, !description && s.communityDescriptionPlaceholder]}>
                    {description || (isCommunity && isAdmin ? t('partnerCommunity.descriptionPlaceholder') : '')}
                  </Text>
                  {isCommunity && isAdmin ? <Icon name="edit-2" size={14} color={Colors.turquoise} /> : null}
                </TouchableOpacity>
              )}
              <View style={s.infoKindBadge}>
                <Text style={s.infoKindText}>{isCommunity ? t('partnerCommunity.memberOnly') : isPlan ? t('chat.communityPlan') : t('chat.officialEvent')}</Text>
              </View>
            </View>

            <View style={s.vibingSection}>
              <View style={s.vibingHeader}>
                <View>
                  <Text style={s.vibingTitle}>{t('chat.vibingWith')}</Text>
                  <Text style={s.vibingSubtitle}>{t('chat.groupCount').replace('{count}', String(memberCount)).replace('{people}', memberCount === 1 ? t('chat.person') : t('chat.people')).replace('{kind}', isPlan ? t('chat.plan') : t('chat.event'))}</Text>
                </View>
                <View style={s.vibingIcon}>
                  <Icon name="users" size={16} color={Colors.turquoise} />
                </View>
              </View>
              {loading ? (
                <View style={s.membersEmpty}>
                  <Text style={s.membersEmptyText}>{t('chat.loadingMembers')}</Text>
                </View>
              ) : (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={s.vibingScroller}
                >
                  {members.map(member => (
                    <TouchableOpacity
                      key={member.id}
                      style={s.memberCard}
                      activeOpacity={0.86}
                      onPress={() => {
                        handleClose();
                        setTimeout(onPeopleHere, 240);
                      }}
                    >
                      <View style={s.memberAvatarWrap}>
                        <ProfileImage uri={member.photo} style={s.memberAvatar} />
                        {member.id === currentUserId ? (
                          <View style={s.memberYouDot} />
                        ) : (
                          <VibeVerifiedBadge verified={member.faceVerified === true} size={52} />
                        )}
                      </View>
                      <Text style={s.memberName} numberOfLines={1}>
                        {member.id === currentUserId ? 'You' : member.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}
              <TouchableOpacity
                style={s.vibingAction}
                onPress={() => {
                  handleClose();
                  setTimeout(onPeopleHere, 240);
                }}
                activeOpacity={0.84}
              >
                <Icon name="zap" size={14} color={Colors.turquoise} />
                <Text style={s.vibingActionText}>{t('chat.openProfiles')}</Text>
                <Icon name="chevron-right" size={15} color="rgba(255,255,255,0.38)" />
              </TouchableOpacity>
            </View>

            {/* Actions */}
            <View style={s.groupActions}>
              <TouchableOpacity
                style={s.deleteChatBtn}
                onPress={() => {
                  handleClose();
                  setTimeout(isCommunity ? onToggleMute : onDeleteChat, 240);
                }}
                activeOpacity={0.8}
              >
                <Icon name={isCommunity ? (isMuted ? 'bell' : 'bell-off') : 'trash-2'} size={15} color="#F87171" />
                <Text style={s.deleteChatText}>{isCommunity ? (isMuted ? t('chat.unmuteChat') : t('chat.muteChat')) : t('chat.deleteChat')}</Text>
              </TouchableOpacity>
              {isCommunity && isAdmin ? (
                <Text style={s.leaveConfirmMsg}>{t('partnerCommunity.ownerCannotLeave')}</Text>
              ) : isAdmin ? (
                !leaveConfirmVisible ? (
                  <TouchableOpacity
                    style={s.leaveBtn}
                    onPress={() => setLeaveConfirmVisible(true)}
                    activeOpacity={0.8}
                  >
                    <Text style={s.leaveBtnText}>{t('chat.leaveChat')}</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={s.leaveConfirm}>
                    <Text style={s.leaveConfirmMsg}>
                      {t('chat.adminCannotLeave')}
                    </Text>
                    <View style={s.leaveConfirmRow}>
                      <TouchableOpacity
                        style={s.leaveConfirmCancel}
                        onPress={() => setLeaveConfirmVisible(false)}
                        activeOpacity={0.8}
                      >
                        <Text style={s.leaveCancelText}>{t('chat.noStay')}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={s.leaveConfirmLeave}
                        onPress={() => { setLeaveConfirmVisible(false); handleClose(); setTimeout(() => onDeletePlan?.(), 240); }}
                        activeOpacity={0.8}
                      >
                        <Text style={s.leaveBtnText}>{t('chat.yesDeletePlan')}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )
              ) : !leaveConfirmVisible ? (
                <TouchableOpacity
                  style={s.leaveBtn}
                  onPress={() => setLeaveConfirmVisible(true)}
                  activeOpacity={0.8}
                >
                  <Text style={s.leaveBtnText}>{t('chat.leaveChat')}</Text>
                </TouchableOpacity>
              ) : (
                <View style={s.leaveConfirm}>
                  <Text style={s.leaveConfirmMsg}>
                    {isCommunity ? t('partnerCommunity.leaveConfirm') : isPlan ? t('chat.leavePlanConfirm') : t('chat.leaveEventConfirm')}
                  </Text>
                  <View style={s.leaveConfirmRow}>
                    <TouchableOpacity
                      style={s.leaveConfirmCancel}
                      onPress={() => setLeaveConfirmVisible(false)}
                      activeOpacity={0.8}
                    >
                      <Text style={s.leaveCancelText}>{t('chat.cancel')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[s.leaveConfirmLeave, leaving && { opacity: 0.5 }]}
                      onPress={leaving ? undefined : handleLeave}
                      activeOpacity={0.8}
                    >
                      <Text style={s.leaveBtnText}>{leaving ? t('chat.leaving') : t('chat.leave')}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          </View>
        </Animated.ScrollView>
      </Animated.View>
    </Modal>
  );
};

// ─── Report Sheet ────────────────────────────────────────────────────────────

const REPORT_REASONS = [
  'Spam',
  'Harassment',
  'Fake profile',
  'Inappropriate content',
  'Scam or unsafe behavior',
  'Other',
] as const;

interface ReportSheetProps {
  visible: boolean;
  targetUserId: string;
  targetUserName: string;
  reportedBy: string;
  chatId: string;
  onClose: () => void;
  onSubmitted: () => void;
}

const ReportSheet: React.FC<ReportSheetProps> = ({
  visible, targetUserId, targetUserName, reportedBy, chatId, onClose, onSubmitted,
}) => {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const { anim, open, close } = useSheetAnim();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (visible) {
      setReason('');
      setDetails('');
      setError('');
      open();
    }
  }, [visible, open]);

  const handleClose = useCallback(() => {
    close(() => onClose());
  }, [close, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!reason) { setError(t('chat.chooseReason')); return; }
    setSubmitting(true);
    setError('');
    try {
      await addDoc(collection(firestore, 'reports_users'), {
        reportedUserId: targetUserId,
        reportedByUserId: reportedBy,
        chatId,
        reason,
        details: details.trim(),
        createdAt: serverTimestamp(),
        status: 'open',
      });
      close(() => { onSubmitted(); onClose(); });
    } catch (err) {
      console.warn('[Tonyte][RN][Report] submit failed', err);
      setError(t('chat.reportFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [reason, targetUserId, reportedBy, chatId, details, close, onSubmitted, onClose, t]);

  if (!visible) return null;

  const slideStyle = {
    transform: [{
      translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [600, 0] }),
    }],
  };

  return (
    <Modal visible transparent animationType="none" onRequestClose={handleClose} statusBarTranslucent>
      <View style={s.sheetOverlay}>
        <Backdrop onPress={handleClose} />
        <Animated.View style={[s.reportSheet, slideStyle, { paddingBottom: insets.bottom + 16 }]}>
          {/* Header */}
          <View style={s.reportHeader}>
            <View>
              <Text style={s.reportSafetyLabel}>{t('chat.safety')}</Text>
              <Text style={s.reportTitle} numberOfLines={2}>{t('chat.reportPerson').replace('{name}', targetUserName)}</Text>
            </View>
            <TouchableOpacity style={s.sheetCloseBtn} onPress={handleClose} disabled={submitting}>
              <Icon name="x" size={18} color={Colors.textPrimary} />
            </TouchableOpacity>
          </View>

          {/* Reasons */}
          <View style={s.reasonsGrid}>
            {REPORT_REASONS.map(opt => (
              <TouchableOpacity
                key={opt}
                style={[s.reasonBtn, reason === opt && s.reasonBtnSelected]}
                onPress={() => setReason(opt)}
                activeOpacity={0.8}
              >
                <Text style={[s.reasonBtnText, reason === opt && s.reasonBtnTextSelected]}>
                  {t(`chat.reason${opt === 'Spam' ? 'Spam' : opt === 'Harassment' ? 'Harassment' : opt === 'Fake profile' ? 'Fake' : opt === 'Inappropriate content' ? 'Content' : opt === 'Scam or unsafe behavior' ? 'Scam' : 'Other'}`)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Details */}
          <TextInput
            style={s.reportDetails}
            value={details}
            onChangeText={setDetails}
            placeholder={t('chat.optionalDetails')}
            placeholderTextColor="rgba(255,255,255,0.30)"
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />

          {Boolean(error) && <Text style={s.reportError}>{error}</Text>}

          {/* Buttons */}
          <View style={s.reportBtnRow}>
            <TouchableOpacity style={s.reportCancelBtn} onPress={handleClose} disabled={submitting} activeOpacity={0.8}>
              <Text style={s.reportCancelText}>{t('chat.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.reportSubmitBtn, submitting && { opacity: 0.6 }]}
              onPress={handleSubmit}
              disabled={submitting}
              activeOpacity={0.8}
            >
              <Text style={s.reportSubmitText}>{submitting ? t('chat.sending') : t('chat.submitReport')}</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

// ─── Direct Chat Actions Sheet ───────────────────────────────────────────────

interface DirectActionsProps {
  visible: boolean;
  onClose: () => void;
  onViewProfile: () => void;
  onReport: () => void;
  onBlock: () => void;
  onDeleteChat: () => void;
  isMuted: boolean;
  onToggleMute: () => void;
}

const DirectActionsSheet: React.FC<DirectActionsProps> = ({ visible, onClose, onViewProfile, onReport, onBlock, onDeleteChat, isMuted, onToggleMute }) => {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const { anim, open, close } = useSheetAnim();

  useEffect(() => {
    if (visible) open();
  }, [visible, open]);

  const handleClose = useCallback(() => {
    close(() => onClose());
  }, [close, onClose]);

  if (!visible) return null;

  const slideStyle = {
    transform: [{
      translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [300, 0] }),
    }],
    opacity: anim,
  };

  return (
    <Modal visible transparent animationType="none" onRequestClose={handleClose} statusBarTranslucent>
      <View style={s.sheetOverlay}>
        <Backdrop onPress={handleClose} />
        <Animated.View style={[s.actionsSheet, slideStyle, { paddingBottom: insets.bottom + 16 }]}>
          <TouchableOpacity style={s.actionRow} onPress={() => { handleClose(); setTimeout(onViewProfile, 240); }} activeOpacity={0.8}>
            <Icon name="user" size={16} color="rgba(255,255,255,0.85)" />
            <Text style={s.actionRowText}>{t('chat.viewProfileTitle')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.actionRow} onPress={() => { handleClose(); setTimeout(onReport, 240); }} activeOpacity={0.8}>
            <Icon name="flag" size={16} color="rgba(255,255,255,0.85)" />
            <Text style={s.actionRowText}>{t('chat.reportUser')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.actionRow} onPress={() => { handleClose(); setTimeout(onToggleMute, 240); }} activeOpacity={0.8}>
            <Icon name={isMuted ? 'bell-off' : 'bell'} size={16} color="rgba(255,255,255,0.85)" />
            <Text style={s.actionRowText}>{isMuted ? t('chat.unmuteChat') : t('chat.muteChat')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.actionRow} onPress={() => { handleClose(); setTimeout(onBlock, 240); }} activeOpacity={0.8}>
            <Icon name="slash" size={16} color="#F87171" />
            <Text style={[s.actionRowText, s.actionDangerText]}>{t('chat.blockUser')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.actionRow} onPress={() => { handleClose(); setTimeout(onDeleteChat, 240); }} activeOpacity={0.8}>
            <Icon name="trash-2" size={16} color="#F87171" />
            <Text style={[s.actionRowText, s.actionDangerText]}>{t('chat.deleteChat')}</Text>
          </TouchableOpacity>
        </Animated.View>
      </View>
    </Modal>
  );
};

// ─── Chat List ───────────────────────────────────────────────────────────────

interface ListProps {
  userId: string | null;
  scrollGesture?: NativeGesture;
  focusMessagesTab?: MessagesTabFocus;
}

type MessagesTab = 'messages' | 'notifications' | 'communities';

const DesktopConversation: React.FC<{ chat: ChatItem; userId: string | null }> = ({ chat, userId }) => {
  const { theme } = useTheme();
  const { t } = useI18n();
  const { messages, loading } = useChatMessages(chat.id, chat.kind ?? 'match');
  const ownProfile = useUserProfile(userId);
  const { venues: ownedPartnerVenues } = useOwnedPartnerVenues(chat.kind === 'community' ? userId : null);
  const sendingPartner = chat.kind === 'community' ? ownedPartnerVenues.find(venue => venue.id === chat.id) : undefined;
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<FlatList<ChatMessage>>(null);
  const title = chat.eventTitle || chat.planTitle || chat.name || 'Chat';

  useEffect(() => {
    if (!userId) return;
    setActiveChatId(chat.id);
    void markChatReadForUser({ chatId: chat.id, kind: chat.kind ?? 'match', userId });
    return () => setActiveChatId(null);
  }, [chat.id, chat.kind, userId]);

  useEffect(() => {
    if (messages.length) requestAnimationFrame(() => listRef.current?.scrollToEnd({ animated: false }));
  }, [messages.length]);

  const send = useCallback(async () => {
    const value = text.trim();
    if (!value || !userId || sending) return;
    setText('');
    setSending(true);
    try {
      await sendChatMessage({
        chatId: chat.id,
        kind: chat.kind ?? 'match',
        text: value,
        senderId: userId,
        senderName: sendingPartner?.name || auth.currentUser?.displayName || 'Tonyte user',
        senderPhotoURL: sendingPartner?.logoUrl || ownProfile?.profileImage || auth.currentUser?.photoURL || undefined,
      });
    } catch (error) {
      setText(value);
      showMessage({ message: t('chat.messageSendFailed', 'Message failed to send'), type: 'danger' });
      console.warn('[Tonyte][Web][DesktopChat] send failed', { chatId: chat.id, error });
    } finally {
      setSending(false);
    }
  }, [chat.id, chat.kind, ownProfile?.profileImage, sending, sendingPartner?.logoUrl, sendingPartner?.name, t, text, userId]);

  return (
    <View style={[s.desktopConversation, { backgroundColor: theme.colors.background }]}> 
      <View style={[s.desktopConversationHeader, { borderColor: theme.colors.border }]}> 
        <ProfileImage uri={chat.photo} style={s.desktopConversationAvatar} />
        <View style={s.desktopConversationHeading}>
          <Text style={[s.desktopConversationTitle, { color: theme.colors.textPrimary }]} numberOfLines={1}>{title}</Text>
          <Text style={[s.desktopConversationSubtitle, { color: theme.colors.textMuted }]}>{chat.participantIds.length} members</Text>
        </View>
      </View>
      {loading ? <LoadingSpinner appearanceAware /> : (
        <FlatList
          ref={listRef}
          data={messages}
          keyExtractor={item => item.id}
          style={s.desktopMessageList}
          contentContainerStyle={s.desktopMessageContent}
          onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: false })}
          renderItem={({ item }) => {
            const mine = item.senderId === userId;
            return (
              <View style={[s.desktopMessageRow, mine && s.desktopMessageRowMine]}>
                {!mine ? <LiveProfileImage userId={item.senderId} fallbackUri={item.senderPhotoURL} style={s.desktopMessageAvatar} /> : null}
                <View style={s.desktopMessageColumn}>
                  {!mine ? <Text style={[s.desktopMessageSender, { color: theme.colors.textMuted }]}>{item.senderName}</Text> : null}
                  <View style={[s.desktopMessageBubble, mine ? s.desktopMessageBubbleMine : { backgroundColor: theme.colors.surfaceElevated, borderColor: theme.colors.border }]}> 
                    {item.imageUrl ? <Image source={{ uri: item.imageUrl }} style={s.desktopMessageImage} /> : null}
                    {item.gif?.url ? <Image source={{ uri: item.gif.url }} style={s.desktopMessageImage} /> : null}
                    {item.text ? <Text style={[s.desktopMessageText, { color: mine ? '#FFFFFF' : theme.colors.textPrimary }]}>{item.text}</Text> : null}
                  </View>
                  <Text style={[s.desktopMessageTime, { color: theme.colors.textMuted }]}>{formatRelativeTime(toEpochMs(item.createdAt))}</Text>
                </View>
              </View>
            );
          }}
          ListEmptyComponent={<View style={s.desktopMessageEmpty}><Text style={{ color: theme.colors.textMuted }}>{t('chat.noMessages')}</Text></View>}
        />
      )}
      <View style={[s.desktopComposer, { borderColor: theme.colors.border, backgroundColor: theme.colors.surfaceElevated }]}> 
        <TextInput
          value={text}
          onChangeText={setText}
          onSubmitEditing={() => void send()}
          placeholder={t('chat.typeMessage')}
          placeholderTextColor={theme.colors.textMuted}
          style={[s.desktopComposerInput, { color: theme.colors.textPrimary }]}
          returnKeyType="send"
        />
        <TouchableOpacity disabled={!text.trim() || sending} onPress={() => void send()} style={[s.desktopSendButton, (!text.trim() || sending) && s.desktopSendButtonDisabled]}>
          {sending ? <ActivityIndicator size="small" color="#FFFFFF" /> : <Icon name="send" size={17} color="#FFFFFF" />}
        </TouchableOpacity>
      </View>
    </View>
  );
};

export const ChatListScreen: React.FC<ListProps> = ({ userId, scrollGesture, focusMessagesTab }) => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { t } = useI18n();
  const { theme } = useTheme();
  const { width } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && width >= 1024;
  const listThemeStyles = useMemo(() => createChatListThemeStyles(theme), [theme]);
  const { chats, loading } = useChats(userId);
  const [viewerIsAdmin, setViewerIsAdmin] = useState(false);
  useEffect(() => {
    if (!userId) { setViewerIsAdmin(false); return undefined; }
    let active = true;
    getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, userId))
      .then(snapshot => {
        if (!active) return;
        const role = snapshot.data()?.role;
        setViewerIsAdmin(role === 'admin' || role === 'co-admin');
      })
      .catch(() => { if (active) setViewerIsAdmin(false); });
    return () => { active = false; };
  }, [userId]);
  const mutedChatIds = useMutedChatIds(userId);
  const { communities, loading: communitiesLoading } = usePartnerCommunities(userId, mutedChatIds, viewerIsAdmin);
  const { requests: vibeRequests, loading: vibeRequestsLoading } = useIncomingVibeRequests(userId);
  const { requests: sentVibes, loading: sentVibesLoading } = useSentVibeRequests(userId);
  const { requests: planJoinRequests, loading: planJoinRequestsLoading } = useAdminAllPendingRequests(userId);
  const [activeTab, setActiveTab] = useState<MessagesTab>('messages');
  const [search, setSearch] = useState('');
  const [desktopSelectedChatId, setDesktopSelectedChatId] = useState<string | null>(null);

  useEffect(() => {
    if (!isDesktopWeb || desktopSelectedChatId || !chats.length) return;
    setDesktopSelectedChatId(chats[0].id);
  }, [chats, desktopSelectedChatId, isDesktopWeb]);
  const desktopSelectedChat = useMemo(() => chats.find(chat => chat.id === desktopSelectedChatId) || null, [chats, desktopSelectedChatId]);

  // One-shot: a new-Vibe-request push notification (see notificationNav.ts)
  // lands here wanting the "Vibe Requests" sub-tab preselected — this screen
  // has no route params of its own (it's a bottom-tab body, not a stack
  // screen), so MainTabs threads it through as a prop instead, keyed by
  // `nonce` so a second tap while already on this tab still re-fires.
  const lastFocusNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!focusMessagesTab || focusMessagesTab.nonce === lastFocusNonceRef.current) return;
    lastFocusNonceRef.current = focusMessagesTab.nonce;
    setActiveTab('notifications');
  }, [focusMessagesTab]);

  const filtered = search.trim()
    ? chats.filter(c => {
        const q = search.toLowerCase();
        return (
          c.name?.toLowerCase().includes(q) ||
          c.lastMsg?.toLowerCase().includes(q) ||
          c.eventTitle?.toLowerCase().includes(q) ||
          c.planTitle?.toLowerCase().includes(q)
        );
      })
    : chats;

  const activeLoading = activeTab === 'messages'
    ? loading
    : activeTab === 'notifications'
      ? vibeRequestsLoading || sentVibesLoading || planJoinRequestsLoading
      : communitiesLoading;
  const unreadChatThreadCount = chats.filter(chat => (
    (chat.kind === 'match' || chat.kind === 'vibe') &&
    (chat.unread || chat.unreadCount > 0)
  )).length;

  if (activeLoading && activeTab === 'messages') return <LoadingSpinner appearanceAware />;

  const openUserProfile = (targetUserId: string, name?: string) => {
    if (!targetUserId) return;
    navigation.navigate('PublicProfile', { userId: targetUserId, name });
  };

  const renderItem = ({ item }: { item: ChatItem }) => {
    const badge = BADGE_STYLES[item.kind ?? 'match'] ?? BADGE_STYLES.match;
    const isUnread = item.unread || item.unreadCount > 0;
    const showDot = isUnread && !item.isMuted;

    return (
      <TouchableOpacity
        style={[
          s.chatCard,
          isUnread ? s.chatCardUnread : s.chatCardRead,
          isUnread ? listThemeStyles.chatCardUnread : listThemeStyles.chatCardRead,
          isDesktopWeb && desktopSelectedChatId === item.id && s.desktopChatCardSelected,
        ]}
        onPress={() => {
          if (isDesktopWeb) {
            setDesktopSelectedChatId(item.id);
            return;
          }
          navigation.navigate('ChatDetail', {
            chatId: item.id,
            name: item.eventTitle || item.planTitle || item.name || 'Chat',
            kind: item.kind ?? 'match',
            photo: item.photo,
            participantIds: item.participantIds,
          });
        }}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityState={{ selected: isDesktopWeb && desktopSelectedChatId === item.id }}
      >
        <View style={s.avatarWrap}>
          <LinearGradient colors={['rgba(255,255,255,0.18)', 'rgba(38,208,206,0.20)']} style={s.chatAvatarFrame}>
            <ProfileImage uri={item.photo} style={s.chatAvatar} />
          </LinearGradient>
          {(item.kind === 'match' || item.kind === 'vibe') && (
            <VibeVerifiedBadge verified={item.otherUserVibeVerified === true} size={50} />
          )}
          {showDot && <View style={s.unreadDot} />}
        </View>
        <View style={s.chatContent}>
          <View style={s.chatTopRow}>
            <View style={s.badgeRow}>
              <View style={[s.badge, { backgroundColor: badge.bg, borderColor: badge.border }]}>
                <Text style={[s.badgeText, { color: badge.text }]}>{badge.label}</Text>
              </View>
              {(item.kind === 'event' || item.kind === 'plan') && item.participantIds.length > 0 && (
                <View style={s.memberCountRow}>
                  <Icon name="users" size={9} color={theme.colors.iconMuted} />
                  <Text style={[s.memberCountText, listThemeStyles.textMuted]}>{item.participantIds.length}</Text>
                </View>
              )}
            </View>
            {/* item.time (formatChatTime's output) is an already-formatted
                absolute-date STRING (see VibesScreen.tsx's own correct,
                direct display of it) — feeding it back into
                formatRelativeTime here re-parsed that display text as a
                date a second time (`new Date("Sep 6")`, no year, resolving
                to some unrelated year) and produced nonsense like "9131d".
                lastActivityAtMs is the same raw canonical value already
                used for sorting above; using it here too is the fix. */}
            <Text style={[s.chatTime, listThemeStyles.textMuted]}>{formatRelativeTime(item.lastActivityAtMs)}</Text>
          </View>
          <Text style={[s.chatName, isUnread ? s.chatNameUnread : s.chatNameRead, listThemeStyles.textPrimary]} numberOfLines={1}>
            {item.eventTitle || item.planTitle || item.name}
          </Text>
          <Text style={[s.chatLastMsg, isUnread ? s.chatLastMsgUnread : s.chatLastMsgRead, isUnread ? listThemeStyles.textSecondary : listThemeStyles.textMuted]} numberOfLines={1}>
            {item.lastMsg || t('chat.noMessages')}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const list = (
    <FlatList
      style={s.scroller}
      data={filtered}
      scrollEnabled
      nestedScrollEnabled
      keyExtractor={item => item.id}
      renderItem={renderItem}
      contentContainerStyle={[s.listContent, { paddingBottom: insets.bottom + 96 }]}
      showsVerticalScrollIndicator={false}
      ListEmptyComponent={
        search.trim() ? (
          <View style={[s.emptyCard, listThemeStyles.emptyCard]}>
            <Text style={[s.emptyTitle, listThemeStyles.textSecondary]}>{t('chat.noChatsFound')}</Text>
            <Text style={[s.emptySubtitle, listThemeStyles.textMuted]}>{t('chat.tryDifferentKeyword')}</Text>
          </View>
        ) : (
          <View style={[s.emptyCard, listThemeStyles.emptyCard]}>
            <View style={[s.emptyIconWrap, listThemeStyles.emptyIconWrap]}>
              <Icon name="message-circle" size={20} color={theme.colors.iconMuted} />
            </View>
            <Text style={[s.emptyTitle, listThemeStyles.textSecondary]}>{t('chat.chatsAppearHere')}</Text>
            <Text style={[s.emptySubtitle, listThemeStyles.textMuted]}>{t('chat.joinToStart')}</Text>
          </View>
        )
      }
    />
  );

  const renderVibeRequests = () => {
    if (vibeRequestsLoading) {
      return <View style={s.tabLoading}><Text style={s.membersEmptyText}>{t('chat.loadingRequests')}</Text></View>;
    }
    if (!vibeRequests.length) {
      return (
        <View style={[s.emptyCard, listThemeStyles.emptyCard]}>
          <View style={[s.emptyIconWrap, listThemeStyles.emptyIconWrap]}>
            <Icon name="zap" size={20} color={theme.colors.iconMuted} />
          </View>
          <Text style={[s.emptyTitle, listThemeStyles.textSecondary]}>{t('chat.noPendingRequests')}</Text>
          <Text style={[s.emptySubtitle, listThemeStyles.textMuted]}>{t('chat.pendingRequestsSub')}</Text>
        </View>
      );
    }
    return (
      <FlatList
        style={s.scroller}
        data={vibeRequests}
        scrollEnabled
        nestedScrollEnabled
        keyExtractor={item => item.id}
        contentContainerStyle={[s.listContent, { paddingBottom: insets.bottom + 96 }]}
        showsVerticalScrollIndicator={false}
        renderItem={({ item }) => (
          <VibeRequestCard
            request={item}
            currentUserId={userId ?? ''}
            mode="received"
            onOpenProfile={openUserProfile}
            onAccepted={(chatId, otherUserId, otherName) => {
              showMessage({ message: t('chat.nowVibingWith').replace('{name}', otherName), type: 'success' });
              setActiveTab('messages');
              navigation.navigate('ChatDetail', {
                chatId,
                name: otherName,
                kind: 'vibe',
                participantIds: [userId ?? '', otherUserId],
              });
            }}
            onDeclined={() => showMessage({ message: t('chat.requestPassed'), type: 'default' })}
          />
        )}
      />
    );
  };

  const renderSentVibes = () => {
    if (sentVibesLoading) {
      return <View style={s.tabLoading}><Text style={s.membersEmptyText}>{t('chat.loadingSent')}</Text></View>;
    }
    if (!sentVibes.length) {
      return (
        <View style={[s.emptyCard, listThemeStyles.emptyCard]}>
          <View style={[s.emptyIconWrap, listThemeStyles.emptyIconWrap]}>
            <Icon name="send" size={20} color={theme.colors.iconMuted} />
          </View>
          <Text style={[s.emptyTitle, listThemeStyles.textSecondary]}>{t('chat.noSent')}</Text>
          <Text style={[s.emptySubtitle, listThemeStyles.textMuted]}>{t('chat.noSentSub')}</Text>
        </View>
      );
    }
    return (
      <FlatList
        style={s.scroller}
        data={sentVibes}
        scrollEnabled
        nestedScrollEnabled
        keyExtractor={item => item.id}
        contentContainerStyle={[s.listContent, { paddingBottom: insets.bottom + 96 }]}
        showsVerticalScrollIndicator={false}
        renderItem={({ item }) => (
          <VibeRequestCard
            request={item}
            currentUserId={userId ?? ''}
            mode="sent"
            onOpenProfile={openUserProfile}
            onAccepted={() => undefined}
            onDeclined={() => undefined}
            onCancelled={() => showMessage({ message: item.status === 'pending' ? 'Vibe cancelled' : 'Sent vibe removed', type: 'default' })}
          />
        )}
      />
    );
  };

  const renderActivePanel = () => {
    if (activeTab === 'notifications') {
      if (vibeRequestsLoading) return renderVibeRequests();
      if (sentVibesLoading) return renderSentVibes();
      if (planJoinRequestsLoading) return <View style={s.tabLoading}><Text style={s.membersEmptyText}>{t('chat.loadingRequests')}</Text></View>;
      if (activeLoading) return <View style={s.tabLoading}><Text style={s.membersEmptyText}>{t('chat.loadingRequests')}</Text></View>;
      return (
        <ScrollView style={s.scroller} contentContainerStyle={[s.listContent, s.notificationListContent, { paddingBottom: insets.bottom + 96 }]}>
          {planJoinRequests.length ? (
            <>
              <Text style={[s.notificationSectionLabel, listThemeStyles.textMuted]}>{t('planRequests.joinRequests')}</Text>
              {planJoinRequests.map(request => (
                <TouchableOpacity
                  key={`plan-request-${request.id}`}
                  style={[s.planRequestNotification, listThemeStyles.chatCardRead]}
                  activeOpacity={0.82}
                  accessibilityRole="button"
                  onPress={() => userId && navigation.navigate('PlanRequests', {
                    userId,
                    planId: request.planId,
                    highlightRequestId: request.id,
                  })}
                >
                  <View style={s.planRequestNotificationIcon}>
                    <Icon name="user-plus" size={18} color={Colors.turquoise} />
                  </View>
                  <View style={s.planRequestNotificationCopy}>
                    <Text style={[s.planRequestNotificationTitle, listThemeStyles.textPrimary]} numberOfLines={1}>
                      {request.planTitle || t('planRequests.communityPlan')}
                    </Text>
                    <Text style={[s.planRequestNotificationBody, listThemeStyles.textMuted]} numberOfLines={2}>
                      {request.message || t('notifications.newJoinRequestBody')}
                    </Text>
                  </View>
                  <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
                </TouchableOpacity>
              ))}
            </>
          ) : null}
          <Text style={[s.notificationSectionLabel, listThemeStyles.textMuted]}>{t('partnerCommunity.incoming')}</Text>
          {vibeRequests.length ? vibeRequests.map(item => (
            <VibeRequestCard
              key={`incoming-${item.id}`}
              request={item}
              currentUserId={userId ?? ''}
              mode="received"
              onOpenProfile={openUserProfile}
              onAccepted={(chatId, otherUserId, otherName) => {
                showMessage({ message: t('chat.nowVibingWith').replace('{name}', otherName), type: 'success' });
                setActiveTab('messages');
                navigation.navigate('ChatDetail', { chatId, name: otherName, kind: 'vibe', participantIds: [userId ?? '', otherUserId] });
              }}
              onDeclined={() => showMessage({ message: t('chat.requestPassed'), type: 'default' })}
            />
          )) : <Text style={[s.emptySubtitle, listThemeStyles.textMuted]}>{t('chat.noPendingRequests')}</Text>}
          <Text style={[s.notificationSectionLabel, listThemeStyles.textMuted]}>{t('partnerCommunity.outgoing')}</Text>
          {sentVibes.length ? sentVibes.map(item => (
            <VibeRequestCard
              key={`outgoing-${item.id}`}
              request={item}
              currentUserId={userId ?? ''}
              mode="sent"
              onOpenProfile={openUserProfile}
              onAccepted={() => undefined}
              onDeclined={() => undefined}
              onCancelled={() => showMessage({ message: item.status === 'pending' ? 'Vibe cancelled' : 'Sent vibe removed', type: 'default' })}
            />
          )) : <Text style={[s.emptySubtitle, listThemeStyles.textMuted]}>{t('chat.noSent')}</Text>}
        </ScrollView>
      );
    }
    if (activeTab === 'communities') {
      if (communitiesLoading) return <LoadingSpinner appearanceAware />;
      return (
        <FlatList
          style={s.scroller}
          data={communities}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          contentContainerStyle={[s.listContent, { paddingBottom: insets.bottom + 96 }]}
          ListEmptyComponent={
            <View style={[s.emptyCard, listThemeStyles.emptyCard]}>
              <View style={[s.emptyIconWrap, listThemeStyles.emptyIconWrap]}><Icon name="users" size={20} color={theme.colors.iconMuted} /></View>
              <Text style={[s.emptyTitle, listThemeStyles.textSecondary]}>{t('partnerCommunity.emptyTitle')}</Text>
              <Text style={[s.emptySubtitle, listThemeStyles.textMuted]}>{t('partnerCommunity.emptyBody')}</Text>
              <TouchableOpacity style={s.communityDiscoverButton} onPress={() => navigation.navigate('VibeVenues')}>
                <Text style={s.communityDiscoverText}>{t('partnerCommunity.discover')}</Text>
              </TouchableOpacity>
            </View>
          }
        />
      );
    }
    return (
      <>
        <View style={[s.searchWrap, listThemeStyles.searchWrap]}>
          <Icon name="search" size={15} color={theme.colors.iconMuted} />
          <TextInput
            style={[s.searchInput, listThemeStyles.textPrimary]}
            value={search}
            onChangeText={setSearch}
            placeholder={t('chat.searchChats')}
            placeholderTextColor={theme.colors.textMuted}
          />
          {Boolean(search) && (
            <TouchableOpacity onPress={() => setSearch('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Icon name="x" size={14} color={theme.colors.iconMuted} />
            </TouchableOpacity>
          )}
        </View>
        {scrollGesture && !isDesktopWeb ? <GestureDetector gesture={scrollGesture}>{list}</GestureDetector> : list}
      </>
    );
  };

  const conversationList = (
    <View style={isDesktopWeb ? [s.desktopConversationRail, { borderColor: theme.colors.border }] : s.container}>
      <View style={s.listHeader}>
        <Text style={[s.listHeaderTitle, listThemeStyles.textPrimary]}>{t('chat.title')}</Text>
        <Text style={[s.listHeaderSub, listThemeStyles.textMuted]}>{t('chat.subtitle')}</Text>
      </View>
      <View style={[s.messageTabs, listThemeStyles.messageTabs]}>
        {([
          ['messages', t('partnerCommunity.messagesTab'), unreadChatThreadCount],
          ['communities', t('partnerCommunity.communitiesTab'), communities.filter(item => item.unread && !item.isMuted).length],
          ['notifications', t('partnerCommunity.notificationsTab'), planJoinRequests.length + vibeRequests.length + sentVibes.filter(item => item.status === 'pending').length],
        ] as Array<[MessagesTab, string, number]>).map(([key, label, count]) => {
          const selected = activeTab === key;
          return (
            <TouchableOpacity
              key={key}
              style={[s.messageTab, selected && s.messageTabActive, selected && listThemeStyles.messageTabActive]}
              onPress={() => setActiveTab(key)}
              activeOpacity={0.84}
            >
              <Text style={[s.messageTabText, listThemeStyles.textMuted, selected && s.messageTabTextActive, selected && listThemeStyles.textPrimary]} numberOfLines={2}>{label}</Text>
              {count > 0 ? (
                <View style={[s.messageTabBadge, !selected && listThemeStyles.messageTabBadge, selected && s.messageTabBadgeActive]}>
                  <Text style={[s.messageTabBadgeText, !selected && listThemeStyles.messageTabBadgeText, selected && s.messageTabBadgeTextActive]}>{count}</Text>
                </View>
              ) : null}
            </TouchableOpacity>
          );
        })}
      </View>
      {renderActivePanel()}
    </View>
  );

  return (
    <TonyteBackground appearanceAware style={[s.container, listThemeStyles.container, { paddingTop: isDesktopWeb ? 0 : insets.top }]}>
      {isDesktopWeb ? (
        <View style={s.desktopChatShell}>
          {conversationList}
          <View style={[s.desktopChatEmpty, desktopSelectedChat && s.desktopChatActive]}>
            {desktopSelectedChat ? (
              <DesktopConversation chat={desktopSelectedChat} userId={userId} />
            ) : <>
            <View style={[s.desktopChatEmptyIcon, { backgroundColor: theme.colors.accentSecondarySoft, borderColor: theme.colors.accentSecondaryBorder }]}><Icon name="message-circle" size={25} color={theme.colors.accentSecondary} /></View>
            <Text style={[s.desktopChatEmptyTitle, { color: theme.colors.textPrimary }]}>{t('chat.title')}</Text>
            <Text style={[s.desktopChatEmptySub, { color: theme.colors.textSecondary }]}>{t('chat.joinToStart')}</Text>
            </>}
          </View>
          <View style={[s.desktopDetailsRail, { borderColor: theme.colors.border }]}>
            <Text style={[s.desktopDetailsTitle, { color: theme.colors.textPrimary }]}>Details</Text>
            {desktopSelectedChat ? <>
              <ProfileImage uri={desktopSelectedChat.photo} style={s.desktopDetailsAvatar} />
              <Text style={[s.desktopDetailsName, { color: theme.colors.textPrimary }]}>{desktopSelectedChat.eventTitle || desktopSelectedChat.planTitle || desktopSelectedChat.name}</Text>
              <View style={s.desktopDetailsMetaRow}><Icon name="users" size={13} color={theme.colors.textSecondary} /><Text style={[s.desktopDetailsText, { color: theme.colors.textSecondary }]}>{desktopSelectedChat.participantIds.length} members</Text></View>
              <View style={s.desktopDetailsMetaRow}><Icon name="message-square" size={13} color={theme.colors.textSecondary} /><Text style={[s.desktopDetailsText, { color: theme.colors.textSecondary }]}>{(desktopSelectedChat.kind || 'chat').toUpperCase()}</Text></View>
            </> : <Text style={[s.desktopDetailsText, { color: theme.colors.textSecondary }]}>Select a conversation to see its members and context.</Text>}
          </View>
        </View>
      ) : conversationList}
    </TonyteBackground>
  );
};

// ─── Chat Detail ─────────────────────────────────────────────────────────────
// The message-bubble renderer now lives in `src/components/chat/ChatMessageBubble.tsx`
// (imported above) as part of the premium redesign — glass/metallic bubbles,
// grouping, reactions, replies, read receipts, image/venue/location cards.

type DetailProps = NativeStackScreenProps<RootStackParamList, 'ChatDetail' | 'StudiZonePlanChat' | 'StudiZonePlanChatScreen'>;

export const ChatDetailScreen: React.FC<DetailProps> = ({ route }) => {
  const { chatId, name, kind, photo, participantIds: routeParticipantIds, showPeople } = route.params;
  const desktopEmbedded = Boolean((route.params as any).desktopEmbedded);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const insets = useSafeAreaInsets();
  const { language, t } = useI18n();
  const { theme } = useTheme();
  const detailThemeStyles = useMemo(() => createChatDetailThemeStyles(theme), [theme]);

  const currentUserId = auth.currentUser?.uid ?? '';
  const { venues: ownedPartnerVenues } = useOwnedPartnerVenues(kind === 'community' && currentUserId ? currentUserId : null);
  const sendingPartner = kind === 'community' ? ownedPartnerVenues.find(venue => venue.id === chatId) : undefined;
  const currentUserName = sendingPartner?.name || auth.currentUser?.displayName || 'Traveler';
  // Stamp new messages with the current profile picture; auth photoURL can
  // lag behind a picture changed on another device.
  const ownProfile = useUserProfile(currentUserId);
  const currentUserPhoto = sendingPartner?.logoUrl || ownProfile?.profileImage || auth.currentUser?.photoURL || undefined;

  const isGroupChat = kind === 'event' || kind === 'plan' || kind === 'community';
  const participantIds = useMemo(() => routeParticipantIds ?? [], [routeParticipantIds]);
  const [liveParticipantIds, setLiveParticipantIds] = useState<string[]>(participantIds);
  const displayParticipantIds = isGroupChat ? liveParticipantIds : participantIds;

  const { messages, loading: messagesLoading } = useChatMessages(chatId, kind);

  // A Vibe chat is the only kind that can be shared-deleted out from under
  // an open screen (unvibeUser fully deletes the privateChats doc — see
  // functions/index.js). Other kinds only ever get a per-user `deletedFor`
  // flag, which never removes the doc itself, so this is deliberately
  // scoped to kind === 'vibe' only.
  const chatDocExists = useChatDocExists(kind === 'vibe' ? chatId : null, kind);
  const vibeEnded = kind === 'vibe' && !chatDocExists;

  // [ChatEnvDebug] TEMPORARY — see logChatEnvDebug in useProfile.ts.
  useEffect(() => {
    void logChatEnvDebug(chatId, kind);
  }, [chatId, kind]);

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [groupInfoVisible, setGroupInfoVisible] = useState(false);
  const [peoHereVisible, setPeoHereVisible] = useState(showPeople === true);
  const [actionsVisible, setActionsVisible] = useState(false);
  const [reportVisible, setReportVisible] = useState(false);
  const [deleteChatConfirmVisible, setDeleteChatConfirmVisible] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [otherUserFaceVerified, setOtherUserFaceVerified] = useState(false);
  const [chatTranslated, setChatTranslated] = useState(false);
  const [chatTranslating, setChatTranslating] = useState(false);
  const [translatedMessageText, setTranslatedMessageText] = useState<Record<string, string>>({});
  const [gifPickerVisible, setGifPickerVisible] = useState(false);
  const [gifPreviewUrl, setGifPreviewUrl] = useState<string | null>(null);
  const [recordingVoice, setRecordingVoice] = useState(false);
  const [pendingVoiceMessages, setPendingVoiceMessages] = useState<ChatMessage[]>([]);
  // Real WindowInsets(IME)-backed height — see useKeyboardInsets.ts /
  // TonyteKeyboardInsetsModule.kt. Replaces a JS Keyboard-module-based
  // `androidKeyboardVisible` boolean that was proven unreliable on-device:
  // it doesn't update when the keyboard's height changes without a full
  // hide/show cycle (e.g. switching Gboard's QWERTY keyboard to its emoji
  // picker), which is exactly the scenario that needs this the most.
  const androidKeyboardInsets = useKeyboardInsets();

  const isMuted = useSingleChatMuted(currentUserId, chatId);
  const leavingRef = useRef(false);

  const flatRef = useRef<FlatList>(null);
  const cancelledVoiceUploadsRef = useRef<Set<string>>(new Set());
  const [headerHeight, setHeaderHeight] = useState(0);

  const otherUserId = !isGroupChat
    ? participantIds.find(id => id !== currentUserId) ?? ''
    : '';

  // ── Premium redesign additions: presence/typing, reactions, receipts, reply ──
  const otherUserPresence = useUserPresence(otherUserId || null);
  const typingUserIds = useOtherUsersTyping(chatId, kind, currentUserId);
  const { notifyTyping, clearTyping } = useTypingIndicator(chatId, kind, currentUserId || null);
  const { reactionsByMessageId } = useChatReactions(chatId, kind);
  const lastReadAtByUser = useChatLastReadAt(chatId, kind);
  const [reactionTarget, setReactionTarget] = useState<ChatMessage | null>(null);
  const [replyTarget, setReplyTarget] = useState<ChatMessage | null>(null);

  // Shared-event expiry — eventIds confirmed expired/unavailable, either from
  // a cheap synchronous snapshot check (message.sharedEvent.endAt) or a live
  // Firestore lookup (only fired on an actual tap; see handleOpenSharedEvent
  // below). `checkedLiveEventIdsRef` dedupes those one-shot lookups so the
  // same eventId is never fetched twice in one screen lifetime — this is a
  // handful of one-time getDoc calls, never a listener.
  const [expiredEventIds, setExpiredEventIds] = useState<Set<string>>(new Set());
  const checkedLiveEventIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isGroupChat || !chatId) {
      setLiveParticipantIds(participantIds);
      return undefined;
    }
    const source = kind === 'community'
      ? collection(firestore, FIRESTORE_COLLECTIONS.partnerCommunityChats, chatId, 'members')
      : kind === 'plan'
      ? doc(firestore, FIRESTORE_COLLECTIONS.plans, chatId)
      : collection(firestore, FIRESTORE_COLLECTIONS.eventChats, chatId, 'participants');
    const unsubscribe = onSnapshot(
      source as any,
      (snapshot: any) => {
        const nextIds = kind === 'plan'
          ? getPlanMemberIds((snapshot as any).exists() ? (snapshot as any).data() : null)
          : (snapshot as any).docs
            .filter((participantDoc: any) => kind !== 'community' || participantDoc.data()?.status === 'active')
            .map((participantDoc: any) => participantDoc.id);
        setLiveParticipantIds(prev => (
          prev.length === nextIds.length && prev.every((id, index) => id === nextIds[index])
            ? prev
            : nextIds
        ));
      },
      (error: unknown) => {
        console.warn('[Tonyte][RN][Chat] activity participants watch failed', { chatId, kind, error });
        setLiveParticipantIds(participantIds);
      },
    );
    return unsubscribe;
  }, [chatId, isGroupChat, kind, participantIds]);

  useEffect(() => {
    if (!otherUserId) { setOtherUserFaceVerified(false); return; }
    let cancelled = false;
    getUserProfile(otherUserId).then(p => {
      if (!cancelled) setOtherUserFaceVerified(p.faceVerified === true);
    });
    return () => { cancelled = true; };
  }, [otherUserId]);

  useEffect(() => {
    if (!chatId || !currentUserId || isBlocked || vibeEnded) return;
    markChatReadForUser({ chatId, kind, userId: currentUserId }).catch(error => {
      console.warn('[Tonyte][RN][ChatReads] mark read failed', {
        path: `${FIRESTORE_COLLECTIONS.users}/${currentUserId}/chatReads/${chatId}`,
        currentUserId,
        chatId,
        kind,
        error,
      });
    });
  }, [chatId, currentUserId, isBlocked, vibeEnded, kind, messages.length]);

  useEffect(() => {
    if (isGroupChat || !currentUserId || !otherUserId) {
      setIsBlocked(false);
      return undefined;
    }

    const outgoingBlockPath = `${FIRESTORE_COLLECTIONS.blocks}/${currentUserId}_${otherUserId}`;
    const incomingBlockPath = `${FIRESTORE_COLLECTIONS.blocks}/${otherUserId}_${currentUserId}`;
    console.log('[Tonyte][RN][Block] chat block status subscription start', {
      currentUserId,
      otherUserId,
      blockDocumentPaths: [outgoingBlockPath, incomingBlockPath],
      queryConditions: [
        'direct doc read: blockerId == currentUserId && blockedId == otherUserId',
        'direct doc read: blockerId == otherUserId && blockedId == currentUserId',
      ],
    });

    let outgoingBlocked = false;
    let incomingBlocked = false;
    const sync = () => setIsBlocked(outgoingBlocked || incomingBlocked);

    const outgoingUnsubscribe = onSnapshot(
      doc(firestore, FIRESTORE_COLLECTIONS.blocks, `${currentUserId}_${otherUserId}`),
      snapshot => {
        outgoingBlocked = snapshot.exists();
        sync();
      },
      error => {
        console.warn('[Tonyte][RN][Block] outgoing block status subscription failed', {
          currentUserId,
          otherUserId,
          path: outgoingBlockPath,
          queryCondition: 'direct doc read',
          error,
        });
      },
    );

    const incomingUnsubscribe = onSnapshot(
      doc(firestore, FIRESTORE_COLLECTIONS.blocks, `${otherUserId}_${currentUserId}`),
      snapshot => {
        incomingBlocked = snapshot.exists();
        sync();
      },
      error => {
        console.warn('[Tonyte][RN][Block] incoming block status subscription failed', {
          currentUserId,
          otherUserId,
          path: incomingBlockPath,
          queryCondition: 'direct doc read',
          error,
        });
      },
    );

    return () => {
      outgoingUnsubscribe();
      incomingUnsubscribe();
    };
  }, [currentUserId, isGroupChat, otherUserId]);

  // Mark this chat as the active screen so foreground notifications skip it
  useEffect(() => {
    setActiveChatId(chatId);
    return () => setActiveChatId(null);
  }, [chatId]);

  // Auto-exit: leave immediately if current user is removed from memberIds
  // (group chats only), the parent chat doc is hard-deleted, or its cached
  // `expiresAt` has already passed (set server-side when the underlying
  // Event/Plan expires — see functions/chatExpiry.js) even if the periodic
  // cleanup sweep hasn't physically deleted the doc yet.
  useEffect(() => {
    if (!isGroupChat || !currentUserId || !chatId) return undefined;
    if (kind === 'community') {
      return onSnapshot(
        doc(firestore, FIRESTORE_COLLECTIONS.partnerCommunityChats, chatId, 'members', currentUserId),
        snapshot => {
          if (!leavingRef.current && (!snapshot.exists() || snapshot.data()?.status !== 'active')) {
            showMessage({ message: t('partnerCommunity.accessRemoved'), type: 'info' });
            navigation.goBack();
          }
        },
        error => console.warn('[Tonyte][RN][Community] membership watch failed', { chatId, error }),
      );
    }
    const collectionName = kind === 'event'
      ? FIRESTORE_COLLECTIONS.eventChats
      : FIRESTORE_COLLECTIONS.planChats;

    const unsub = onSnapshot(
      doc(firestore, collectionName, chatId),
      snapshot => {
        if (leavingRef.current) return;
        if (!snapshot.exists()) { navigation.goBack(); return; }
        const data = snapshot.data();
        const expiresAt = (data as any)?.expiresAt;
        const expiresAtMs = expiresAt && typeof expiresAt.toMillis === 'function' ? expiresAt.toMillis() : null;
        if (expiresAtMs !== null && expiresAtMs <= Date.now()) {
          leavingRef.current = true;
          showMessage({ message: t(kind === 'event' ? 'chat.eventChatEnded' : 'chat.planChatEnded'), type: 'info' });
          navigation.goBack();
          return;
        }
        const memberIds: string[] = Array.isArray(data.memberIds) ? data.memberIds : [];
        if (!memberIds.includes(currentUserId)) {
          // Reaching here with leavingRef.current still false means this
          // wasn't a voluntary leave (handleLeaveSuccess always sets that
          // ref first) — for a Plan chat, the only other way membership
          // disappears is the admin/creator removing this user (see
          // removePlanMember). Surface that plainly instead of silently
          // bouncing back to a screen change the user never asked for.
          if (kind === 'plan') {
            leavingRef.current = true;
            showMessage({ message: t('chat.removedFromPlan'), type: 'info' });
          }
          navigation.goBack();
        }
      },
      error => console.warn('[Tonyte][RN][Chat] membership watch failed', { chatId, error }),
    );

    if (kind === 'event') {
      const participantUnsub = onSnapshot(
        doc(firestore, FIRESTORE_COLLECTIONS.eventChats, chatId, 'participants', currentUserId),
        snapshot => {
          if (leavingRef.current) return;
          if (!snapshot.exists()) navigation.goBack();
        },
        error => console.warn('[Tonyte][RN][Chat] event participant watch failed', { chatId, error }),
      );
      return () => {
        unsub();
        participantUnsub();
      };
    }

    return unsub;
  }, [chatId, currentUserId, isGroupChat, kind, navigation, t]);

  const handleToggleMute = useCallback(async () => {
    if (!currentUserId) return;
    try {
      if (isMuted) {
        await unmuteChatForUser(currentUserId, chatId);
        showMessage({ message: t('chat.chatUnmuted'), type: 'success' });
      } else {
        await muteChatForUser(currentUserId, chatId);
        showMessage({ message: t('chat.chatMuted'), type: 'success' });
      }
    } catch (err) {
      console.warn('[Tonyte][RN][Mute] toggle failed', { chatId, err });
    }
  }, [chatId, currentUserId, isMuted, t]);

  const handleLeaveSuccess = useCallback(() => {
    leavingRef.current = true;
    setGroupInfoVisible(false);
    navigation.goBack();
    showMessage({ message: t('chat.leftChat'), type: 'success' });
  }, [navigation, t]);

  const openOtherProfile = useCallback(() => {
    if (!otherUserId) return;
    navigation.navigate('PublicProfile', { userId: otherUserId, chatId, name });
  }, [chatId, name, navigation, otherUserId]);

  const confirmBlockUser = useCallback(() => {
    if (!currentUserId || !otherUserId || blocking) return;
    Alert.alert(
      t('chat.blockUserQuestion'),
      t('chat.blockUserMessage').replace('{name}', name || t('chat.thisUser')),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('chat.block'),
          style: 'destructive',
          onPress: async () => {
            setBlocking(true);
            try {
              await blockUserFromChat(currentUserId, otherUserId);
              showMessage({ message: t('chat.userBlocked'), type: 'success' });
              navigation.goBack();
            } catch (error) {
              console.warn('[Tonyte][RN][Block] block failed', { otherUserId, error });
              Alert.alert(t('chat.couldNotBlockUser'), t('common.tryAgain'));
            } finally {
              setBlocking(false);
            }
          },
        },
      ],
    );
  }, [blocking, currentUserId, name, navigation, otherUserId, t]);

  const confirmDeleteChat = useCallback(() => {
    if (!currentUserId) return;
    setDeleteChatConfirmVisible(true);
  }, [currentUserId]);

  const deleteCurrentChat = useCallback(async () => {
    if (!currentUserId) return;
    setDeleteChatConfirmVisible(false);
    try {
      await deleteChatForUser({ chatId, kind, userId: currentUserId });
      showMessage({ message: t('chat.chatDeleted'), type: 'success' });
      navigation.goBack();
    } catch (error) {
      console.warn('[Tonyte][RN][Chat] delete chat failed', { chatId, kind, currentUserId, error });
      Alert.alert(t('chat.couldNotDeleteChat'), t('common.tryAgain'));
    }
  }, [chatId, currentUserId, kind, navigation, t]);

  const confirmDeletePlan = useCallback(() => {
    Alert.alert(
      t('event.deletePlanQuestion'),
      t('chat.deletePlanMessage'),
      [
        { text: t('event.noStay'), style: 'cancel' },
        {
          text: t('event.yesDeletePlan'),
          style: 'destructive',
          onPress: async () => {
            try {
              await Promise.all([
                deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, chatId)),
                deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.planChats, chatId)),
              ]);
              showMessage({ message: t('event.planDeleted'), type: 'success' });
              navigation.goBack();
            } catch (error) {
              console.warn('[Tonyte][RN][Chat] delete plan failed', { chatId, error });
              Alert.alert(t('event.couldNotDeletePlan'), t('common.tryAgain'));
            }
          },
        },
      ],
    );
  }, [chatId, navigation, t]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messages.length > 0) {
      const t = setTimeout(() => flatRef.current?.scrollToEnd({ animated: true }), 80);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [messages.length]);

  useEffect(() => {
    const scrollToLatestMessage = () => {
      requestAnimationFrame(() => {
        setTimeout(() => flatRef.current?.scrollToEnd({ animated: true }), 90);
      });
    };

    const subscriptions = Platform.OS === 'ios'
      ? [
        Keyboard.addListener('keyboardWillShow', scrollToLatestMessage),
        Keyboard.addListener('keyboardWillChangeFrame', scrollToLatestMessage),
      ]
      : [
        Keyboard.addListener('keyboardDidShow', scrollToLatestMessage),
      ];

    return () => subscriptions.forEach(subscription => subscription.remove());
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || !currentUserId || sending || isBlocked || vibeEnded) return;
    setSending(true);
    // [ChatSendDebug] TEMPORARY — input is cleared here, BEFORE the Firestore
    // write below is even attempted, let alone confirmed. See sendChatMessage
    // for whether the write itself actually succeeds.
    console.log('[ChatSendDebug] handleSend: clearing input before persistence', {
      chatId,
      kind,
      senderId: currentUserId,
      textLength: trimmed.length,
    });
    setText('');
    clearTyping();
    const replyTo = replyTarget ? { messageId: replyTarget.id, senderName: replyTarget.senderName, text: replyTarget.text } : undefined;
    setReplyTarget(null);
    try {
      await sendChatMessage({
        chatId,
        kind,
        text: trimmed,
        senderId: currentUserId,
        senderName: currentUserName,
        senderPhotoURL: currentUserPhoto,
        replyTo,
      });
    } catch (err: any) {
      console.error('[ChatSendDebug] handleSend: sendChatMessage threw', {
        chatId,
        kind,
        senderId: currentUserId,
        code: err?.code,
        message: err?.message,
        err,
      });
      console.warn('[Tonyte][RN][Chat] send failed', { chatId, kind, err });
      setText(trimmed);
      // [ChatSendDebug] TEMPORARY — visible failure surface for debugging;
      // the existing silent-restore-to-input behavior above is unchanged.
      Alert.alert('Message failed to send', `${err?.code || 'unknown'}: ${err?.message || String(err)}`);
    } finally {
      setSending(false);
    }
  }, [chatId, clearTyping, currentUserId, currentUserName, currentUserPhoto, isBlocked, vibeEnded, kind, replyTarget, sending, text]);

  const handleSendImage = useCallback(async (uri: string) => {
    if (!currentUserId) return;
    try {
      const { downloadUrl } = await uploadChatImage(chatId, currentUserId, uri);
      await sendChatMessage({
        chatId,
        kind,
        text: '',
        senderId: currentUserId,
        senderName: currentUserName,
        senderPhotoURL: currentUserPhoto,
        imageUrl: downloadUrl,
      });
    } catch (error) {
      console.warn('[Tonyte][RN][Chat] image send failed', { chatId, kind, error });
      showMessage({ message: t('chat.couldNotSendImage', "Couldn't send photo"), type: 'danger' });
    }
  }, [chatId, currentUserId, currentUserName, currentUserPhoto, kind, t]);

  const handlePickPhoto = useCallback(async () => {
    const response = await pickImageFromLibrary({ mediaType: 'photo', quality: 0.7 });
    const uri = response?.assets?.[0]?.uri;
    if (uri) void handleSendImage(uri);
  }, [handleSendImage]);

  const handleTakePhoto = useCallback(async () => {
    const response = await takePhotoWithCamera({ mediaType: 'photo', quality: 0.7 });
    const uri = response?.assets?.[0]?.uri;
    if (uri) void handleSendImage(uri);
  }, [handleSendImage]);

  const handleShareLocation = useCallback(async () => {
    if (!currentUserId) return;
    const snapshot = getLocationSnapshot();
    if (!snapshot.coords) {
      showMessage({ message: t('chat.locationUnavailable', 'Location unavailable right now'), type: 'info' });
      return;
    }
    try {
      await sendChatMessage({
        chatId,
        kind,
        text: '',
        senderId: currentUserId,
        senderName: currentUserName,
        senderPhotoURL: currentUserPhoto,
        sharedVenue: {
          venueId: '',
          name: 'My Location',
          category: 'Shared location',
          lat: snapshot.coords.latitude,
          lng: snapshot.coords.longitude,
        },
      });
    } catch (error) {
      console.warn('[Tonyte][RN][Chat] location send failed', { chatId, kind, error });
      showMessage({ message: t('chat.couldNotSendLocation', "Couldn't share location"), type: 'danger' });
    }
  }, [chatId, currentUserId, currentUserName, currentUserPhoto, kind, t]);

  const handleSendGif = useCallback(async (gif: ChatGif) => {
    if (!currentUserId || isBlocked || vibeEnded) return;
    const replyTo = replyTarget ? { messageId: replyTarget.id, senderName: replyTarget.senderName, text: replyTarget.text } : undefined;
    setReplyTarget(null);
    try {
      await sendChatMessage({
        chatId,
        kind,
        text: '',
        senderId: currentUserId,
        senderName: currentUserName,
        senderPhotoURL: currentUserPhoto,
        gif,
        replyTo,
      });
    } catch (error) {
      console.warn('[Tonyte][RN][GIF] send failed', { chatId, kind, error });
      showMessage({ message: t('chat.gifSendFailed'), type: 'danger' });
    }
  }, [chatId, currentUserId, currentUserName, currentUserPhoto, isBlocked, vibeEnded, kind, replyTarget, t]);

  const uploadPendingVoiceMessage = useCallback(async (localMessage: ChatMessage) => {
    if (!currentUserId || !localMessage.audioUrl) return;
    cancelledVoiceUploadsRef.current.delete(localMessage.id);
    setPendingVoiceMessages(prev => prev.map(item => (
      item.id === localMessage.id ? { ...item, status: 'uploading', uploadProgress: 0.02 } : item
    )));

    try {
      const { downloadUrl, storagePath } = await uploadChatAudio(
        chatId,
        currentUserId,
        localMessage.audioUrl,
        progress => {
          setPendingVoiceMessages(prev => prev.map(item => (
            item.id === localMessage.id ? { ...item, uploadProgress: progress } : item
          )));
        },
      );
      if (cancelledVoiceUploadsRef.current.has(localMessage.id)) return;
      await sendChatMessage({
        chatId,
        kind,
        text: '',
        senderId: currentUserId,
        senderName: currentUserName,
        senderPhotoURL: currentUserPhoto,
        audioUrl: downloadUrl,
        audioStoragePath: storagePath,
        audioMimeType: localMessage.audioMimeType,
        duration: localMessage.duration || 0,
        waveformData: localMessage.waveformData || [],
      });
      setPendingVoiceMessages(prev => prev.filter(item => item.id !== localMessage.id));
    } catch (error) {
      if (cancelledVoiceUploadsRef.current.has(localMessage.id)) return;
      console.warn('[Tonyte][RN][Voice] upload failed', { chatId, kind, error });
      setPendingVoiceMessages(prev => prev.map(item => (
        item.id === localMessage.id ? { ...item, status: 'failed', uploadProgress: 0 } : item
      )));
      showMessage({ message: t('chat.voiceUploadFailed'), type: 'warning' });
    }
  }, [chatId, currentUserId, currentUserName, currentUserPhoto, kind, t]);

  const handleVoiceRecorded = useCallback((payload: { uri: string; duration: number; waveformData: number[]; mimeType?: string }) => {
    setRecordingVoice(false);
    const localMessage: ChatMessage = {
      id: `local-voice-${Date.now()}`,
      chatId,
      type: 'voice',
      text: '',
      senderId: currentUserId,
      senderName: currentUserName,
      senderPhotoURL: currentUserPhoto,
      createdAt: new Date().toISOString(),
      audioUrl: payload.uri,
      audioMimeType: payload.mimeType,
      duration: payload.duration,
      waveformData: payload.waveformData,
      status: 'uploading',
      uploadProgress: 0.02,
    };
    setPendingVoiceMessages(prev => [...prev, localMessage]);
    void uploadPendingVoiceMessage(localMessage);
  }, [chatId, currentUserId, currentUserName, currentUserPhoto, uploadPendingVoiceMessage]);

  const handleCancelVoiceUpload = useCallback((message: ChatMessage) => {
    cancelledVoiceUploadsRef.current.add(message.id);
    setPendingVoiceMessages(prev => prev.filter(item => item.id !== message.id));
  }, []);

  const handleRetryVoiceUpload = useCallback((message: ChatMessage) => {
    void uploadPendingVoiceMessage(message);
  }, [uploadPendingVoiceMessage]);

  const handleOpenGifPicker = useCallback(() => {
    if (isBlocked || vibeEnded) return;
    setGifPickerVisible(true);
  }, [isBlocked, vibeEnded]);

  // Tracks whether the last mic-permission attempt landed on "blocked"
  // (NEVER_ASK_AGAIN) so the AppState resume effect below knows there's
  // something worth silently re-checking when the user comes back from
  // Settings — avoids an unconditional permission check on every single
  // foreground resume for chats that never touched voice recording.
  const micBlockedRef = useRef(false);

  const handleOpenVoiceRecorder = useCallback(async () => {
    if (isBlocked || vibeEnded || recordingVoice) return;
    // On web, requestMicrophonePermission() below resolves 'granted'
    // unconditionally (it defers to the OS-level prompt the same way iOS
    // does) — the real browser mic-permission dialog is triggered by
    // ChatAudioService.web.ts's getUserMedia() call once the composer mounts
    // and starts recording. A denial there rejects with a message
    // VoiceRecorderComposer.tsx's existing isPermissionError check already
    // recognizes, showing the same "microphone access needed" alert as native.
    const permission = await requestMicrophonePermission();
    if (__DEV__) console.log('[Tonyte][RN][Voice] mic permission before recording:', permission);
    micBlockedRef.current = permission === 'blocked';
    // requestMicrophonePermission already shows the right contextual alert
    // (denied vs. blocked/"Open Settings") on Android when not granted; on
    // iOS it always resolves 'granted' here and the native recorder itself
    // handles the real system prompt. No second alert needed either way.
    if (permission !== 'granted') return;
    setRecordingVoice(true);
  }, [isBlocked, vibeEnded, recordingVoice]);

  // Re-checks microphone permission when the app comes back to the
  // foreground (e.g. the user tapped "Open Settings" from the blocked-
  // permission alert, enabled the mic there, then switched back) so the UI
  // reflects the real OS-level state immediately — the very next mic tap
  // starts recording without a stale "still blocked" alert, no app restart
  // needed. Only fires the check when we know a prior attempt was actually
  // blocked, and only logs in development.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextState => {
      if (nextState !== 'active' || !micBlockedRef.current) return;
      checkMicrophonePermissionSilently().then(status => {
        if (status === 'granted') {
          micBlockedRef.current = false;
          showMessage({ message: t('chat.microphoneNowEnabled'), type: 'success', duration: 2000 });
        }
      });
    });
    return () => subscription.remove();
  }, [t]);

  const handleToggleReaction = useCallback((messageId: string, emoji: string) => {
    if (!currentUserId) return;
    const currentEmoji = reactionsByMessageId.get(messageId)?.find(r => r.uid === currentUserId)?.emoji;
    toggleMessageReaction({ chatId, kind, messageId, uid: currentUserId, emoji, currentEmoji }).catch(error => {
      console.warn('[Tonyte][RN][ChatReactions] toggle failed', { chatId, kind, messageId, error });
    });
  }, [chatId, currentUserId, kind, reactionsByMessageId]);

  const handleLongPressMessage = useCallback((message: ChatMessage) => {
    setReactionTarget(message);
  }, []);

  const toggleChatTranslation = useCallback(async () => {
    if (chatTranslated) {
      setChatTranslated(false);
      return;
    }
    if (language === 'en' || chatTranslating) return;
    setChatTranslating(true);
    try {
      const translated = await translateVisibleChatMessages({
        messages: messages
          .filter(message => message.type !== 'vibe_icebreaker')
          .map(message => ({ id: message.id, text: message.text })),
        targetLanguage: language,
      });
      setTranslatedMessageText(translated);
      setChatTranslated(true);
    } catch {
      showMessage({ message: t('chat.translationFailed'), type: 'warning' });
    } finally {
      setChatTranslating(false);
    }
  }, [chatTranslated, chatTranslating, language, messages, t]);

  // Confirms an event is still live before navigating — EventDetailScreen
  // resolves events purely from useFeed()'s activities list, which already
  // excludes expired/removed events by design, so navigating to a dead
  // eventId there means `activity` never resolves and the screen is stuck
  // on its loading spinner forever. Cached per eventId in
  // checkedLiveEventIdsRef so repeat taps on the same shared-event bubble
  // never re-fetch — this is a one-shot read per distinct event actually
  // tapped, not a listener.
  const verifyEventStillOpenable = useCallback(async (eventId: string): Promise<boolean> => {
    if (checkedLiveEventIdsRef.current.has(eventId)) {
      return !expiredEventIds.has(eventId);
    }
    try {
      const snaps = await Promise.all(
        PUBLIC_ACTIVITY_COLLECTIONS.map(collectionName => getDoc(doc(firestore, collectionName, eventId))),
      );
      checkedLiveEventIdsRef.current.add(eventId);
      const found = snaps.find(snap => snap.exists());
      if (!found) {
        // Document no longer exists in any known collection — unavailable,
        // not "still loading". Never let this fall through to a screen
        // that can only show an infinite spinner for it.
        setExpiredEventIds(prev => new Set(prev).add(eventId));
        return false;
      }
      const activity = { id: found.id, ...found.data() } as TonyteActivity;
      const expired = isActivityExpired(activity);
      if (expired) setExpiredEventIds(prev => new Set(prev).add(eventId));
      return !expired;
    } catch (error) {
      console.warn('[Tonyte][RN][Chat] shared event validity check failed', { eventId, error });
      // A network/permission hiccup here shouldn't strand the user on a
      // false "expired" state — let navigation proceed as before; worst
      // case is the pre-existing (unrelated) EventDetailScreen behavior.
      return true;
    }
  }, [expiredEventIds]);

  const handleOpenSharedEvent = useCallback((eventId: string) => {
    if (!eventId || expiredEventIds.has(eventId)) return;
    void verifyEventStillOpenable(eventId).then(openable => {
      if (!openable) {
        showMessage({ message: t('chat.eventExpiredToast'), type: 'info' });
        return;
      }
      // No organizerId available here — only a bare eventId is carried by a
      // shared-event chat bubble (ChatMessage.sharedEvent.eventId).
      trackEventDetailOpen({ eventId, source: ANALYTICS_SOURCES.SHARED_LINK });
      navigation.navigate('EventDetail', { eventId });
    });
  }, [expiredEventIds, navigation, t, verifyEventStillOpenable]);

  const handleOpenSharedVenue = useCallback((venueId: string) => {
    if (venueId) navigation.navigate('VenueProfile', { venueId });
  }, [navigation]);

  const handleOpenSharedLocation = useCallback((venue: NonNullable<ChatMessage['sharedVenue']>) => {
    const { lat, lng, address } = venue;
    if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      showMessage({ message: t('chat.invalidSharedLocation'), type: 'info' });
      return;
    }
    navigation.navigate('Main', {
      initialTab: 'Map',
      focusMapLocation: { lat, lng, address, nonce: Date.now() },
    });
  }, [navigation, t]);

  // Other participants' most-recent read timestamp — for "Seen"/"Seen Xm
  // ago" on my own outgoing bubbles. For group chats this is the max across
  // every other member (i.e. "seen" once anyone else has read it, labeled
  // with whichever member read most recently).
  const { maxMs: otherParticipantsMaxReadAt, raw: otherParticipantsMaxReadAtRaw } = useMemo(() => {
    const otherIds = displayParticipantIds.filter(id => id !== currentUserId);
    let maxMs = 0;
    let raw: string | { seconds: number; nanoseconds: number } | null = null;
    otherIds.forEach(id => {
      const value = toEpochMs(lastReadAtByUser[id]);
      if (value > maxMs) {
        maxMs = value;
        raw = lastReadAtByUser[id];
      }
    });
    return { maxMs, raw };
  }, [currentUserId, displayParticipantIds, lastReadAtByUser]);

  type ListRow =
    | { kind: 'separator'; id: string; label: string }
    | { kind: 'message'; id: string; message: ChatMessage; showAvatarAndName: boolean };

  const displayMessages = useMemo(() => [...messages, ...pendingVoiceMessages], [messages, pendingVoiceMessages]);

  const listRows = useMemo((): ListRow[] => {
    const rows: ListRow[] = [];
    displayMessages.forEach((message, index) => {
      const previous = displayMessages[index - 1];
      // A join/leave notice between two messages breaks the sender grouping.
      const previousForGrouping = previous?.type === 'member_event' ? undefined : previous;
      if (!isSameCalendarDay(message.createdAt, previous?.createdAt)) {
        rows.push({ kind: 'separator', id: `sep-${message.id}`, label: formatDateSeparatorLabel(message.createdAt) });
      }
      const grouped = message.type !== 'vibe_icebreaker'
        && shouldGroupWithPrevious(message.senderId, message.createdAt, previousForGrouping?.senderId, previousForGrouping?.createdAt)
        && (previousForGrouping ? isSameCalendarDay(message.createdAt, previousForGrouping.createdAt) : false);
      rows.push({ kind: 'message', id: message.id, message, showAvatarAndName: !grouped });
    });
    return rows;
  }, [displayMessages]);

  const renderRow = useCallback(({ item: row }: { item: ListRow }) => {
    if (row.kind === 'separator') {
      return <DateSeparatorPill label={row.label} />;
    }
    const message = row.message;
    if (message.type === 'member_event') {
      const name = message.senderId === currentUserId ? t('chat.you') : message.senderName;
      const label = t(message.memberEvent === 'left' ? 'chat.memberLeft' : 'chat.memberJoined').replace('{name}', name);
      return <MemberEventNotice label={label} joined={message.memberEvent !== 'left'} />;
    }
    const isMe = message.senderId === currentUserId;
    const seenLabel = isMe && otherParticipantsMaxReadAt > 0 && otherParticipantsMaxReadAt >= toEpochMs(message.createdAt)
      ? formatSeenReceipt(otherParticipantsMaxReadAtRaw)
      : undefined;
    const sharedEventId = message.sharedEvent?.eventId;
    const eventExpired = sharedEventId
      ? expiredEventIds.has(sharedEventId) ||
        (message.sharedEvent?.endAt ? new Date(message.sharedEvent.endAt).getTime() < Date.now() : false)
      : false;
    return (
      <ChatMessageBubble
        item={message}
        isMe={isMe}
        translatedText={chatTranslated ? translatedMessageText[message.id] : undefined}
        t={t}
        showAvatarAndName={row.showAvatarAndName}
        preferSnapshotIdentity={kind === 'community'}
        reactions={reactionsByMessageId.get(message.id) || NO_REACTIONS}
        seenLabel={seenLabel}
        onOpenSharedEvent={handleOpenSharedEvent}
        onOpenSenderProfile={(senderId) => navigation.navigate('PublicProfile', { userId: senderId })}
        onOpenSharedVenue={handleOpenSharedVenue}
        onOpenSharedLocation={handleOpenSharedLocation}
        eventExpired={eventExpired}
        onOpenGif={setGifPreviewUrl}
        onRetryVoiceUpload={handleRetryVoiceUpload}
        onCancelVoiceUpload={handleCancelVoiceUpload}
        onLongPress={handleLongPressMessage}
      />
    );
  }, [chatTranslated, currentUserId, expiredEventIds, handleCancelVoiceUpload, handleLongPressMessage, handleOpenSharedEvent, handleOpenSharedLocation, handleOpenSharedVenue, handleRetryVoiceUpload, kind, navigation, otherParticipantsMaxReadAt, otherParticipantsMaxReadAtRaw, reactionsByMessageId, t, translatedMessageText]);

  const isAnyoneElseTyping = typingUserIds.length > 0;
  const myReactionOnTarget = reactionTarget
    ? reactionsByMessageId.get(reactionTarget.id)?.find(r => r.uid === currentUserId)?.emoji
    : undefined;

  return (
    <View style={[s.detailRoot, detailThemeStyles.detailRoot]}>
      <ChatAtmosphere />

      {/* ── Header ──────────────────────────────── */}
      <View
        style={[s.detailHeaderWrap, detailThemeStyles.detailHeaderWrap, { paddingTop: desktopEmbedded ? 0 : insets.top }]}
        onLayout={e => setHeaderHeight(e.nativeEvent.layout.height)}
      >
        <ChatHeader
          name={name}
          photo={photo}
          isGroupChat={isGroupChat}
          memberCount={displayParticipantIds.length}
          isVerified={otherUserFaceVerified}
          presence={otherUserPresence}
          isOtherUserTyping={isAnyoneElseTyping}
          onPressBack={() => navigation.goBack()}
          onPressCenter={isGroupChat ? () => setGroupInfoVisible(true) : openOtherProfile}
          onPressOptions={isGroupChat ? () => setGroupInfoVisible(true) : () => setActionsVisible(true)}
          centerDisabled={!isGroupChat && !otherUserId}
          hideBack={desktopEmbedded}
        />
      </View>

      {/* Android: windowSoftInputMode="adjustResize" does not resize the
          root window under mandatory edge-to-edge (Android 15+, targetSdk
          36) — confirmed on-device, Dimensions('window') stays identical
          to ('screen') while the keyboard is open. So instead of relying
          on window resize, Android's bottom padding comes from
          useKeyboardInsets() (TonyteKeyboardInsetsModule.kt), which reads
          the real WindowInsets IME type directly — this also fires on
          keyboard-TYPE switches (e.g. Gboard QWERTY -> emoji), which RN's
          own JS Keyboard module does not re-emit for. iOS keeps its own
          pre-existing 'padding' + headerHeight behavior, unaffected. */}
      <KeyboardAvoidingView
        style={[
          s.detailKeyboardBody,
          Platform.OS === 'android' && { paddingBottom: androidKeyboardInsets.height },
        ]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? headerHeight : 0}
      >
        {language !== 'en' ? (
          <View style={s.translateChatBar}>
            <TouchableOpacity
              style={[s.translateChatButton, detailThemeStyles.translateChatButton, chatTranslating && { opacity: 0.7 }]}
              onPress={toggleChatTranslation}
              disabled={chatTranslating}
              activeOpacity={0.82}
            >
              <Icon name="globe" size={13} color={Colors.turquoise} />
              <Text style={s.translateChatText}>
                {chatTranslating ? t('chat.translatingChat') : chatTranslated ? t('chat.showOriginal') : t('chat.translateChat')}
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {/* ── Messages ────────────────────────────── */}
        {vibeEnded ? (
          <View style={s.blockedChatState}>
            <View style={s.blockedChatIcon}>
              <Icon name="zap-off" size={20} color="#F87171" />
            </View>
            <Text style={[s.blockedChatTitle, detailThemeStyles.textPrimary]}>{t('chat.vibeEnded')}</Text>
            <Text style={[s.blockedChatText, detailThemeStyles.textSecondary]}>{t('chat.vibeEndedBody')}</Text>
          </View>
        ) : isBlocked ? (
          <View style={s.blockedChatState}>
            <View style={s.blockedChatIcon}>
              <Icon name="slash" size={20} color="#F87171" />
            </View>
            <Text style={[s.blockedChatTitle, detailThemeStyles.textPrimary]}>{t('chat.youBlockedUser')}</Text>
            <Text style={[s.blockedChatText, detailThemeStyles.textSecondary]}>{t('chat.unblockInSettings')}</Text>
          </View>
        ) : messagesLoading ? (
          <View style={s.loadingWrap}>
            <LoadingSpinner />
          </View>
        ) : (
          <FlatList
            ref={flatRef}
            style={s.scroller}
            data={listRows}
            keyExtractor={row => row.id}
            renderItem={renderRow}
            contentContainerStyle={[s.msgList, messages.length === 0 && s.msgListEmpty]}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            onContentSizeChange={() => flatRef.current?.scrollToEnd({ animated: false })}
            initialNumToRender={20}
            maxToRenderPerBatch={16}
            windowSize={8}
            removeClippedSubviews={Platform.OS === 'android'}
            ListFooterComponent={isAnyoneElseTyping ? <TypingIndicator /> : null}
            ListEmptyComponent={
              <View style={[s.emptyMsgCard, detailThemeStyles.emptyMsgCard]}>
                <Text style={[s.emptyMsgText, detailThemeStyles.textSecondary]}>{t('chat.noMessages')}</Text>
              </View>
            }
          />
        )}

        {/* ── Input bar ───────────────────────────── */}
        {recordingVoice ? (
          <VoiceRecorderComposer
            onCancel={() => setRecordingVoice(false)}
            onSend={handleVoiceRecorded}
          />
        ) : (
          <ChatInputBar
            value={text}
            onChangeText={setText}
            onSend={handleSend}
            onTyping={notifyTyping}
            disabled={isBlocked || vibeEnded}
            placeholder={vibeEnded ? t('chat.vibeEnded') : isBlocked ? t('chat.userBlocked') : t('chat.typeMessage')}
            sending={sending}
            replyTarget={replyTarget ? { senderName: replyTarget.senderId === currentUserId ? t('chat.you') : replyTarget.senderName, text: replyTarget.text } : null}
            onDismissReply={() => setReplyTarget(null)}
            onPickPhoto={handlePickPhoto}
            onTakePhoto={handleTakePhoto}
            onShareLocation={handleShareLocation}
            onGif={handleOpenGifPicker}
            onVoice={handleOpenVoiceRecorder}
          />
        )}
        <View
          style={{
            height: Platform.OS === 'android' && androidKeyboardInsets.visible
              ? Spacing.sm
              : insets.bottom + Spacing.sm,
          }}
        />

        <ReactionBar
          visible={Boolean(reactionTarget)}
          currentEmoji={myReactionOnTarget}
          onSelect={emoji => {
            if (reactionTarget) handleToggleReaction(reactionTarget.id, emoji);
          }}
          onReply={reactionTarget?.type !== 'vibe_icebreaker' ? () => {
            if (reactionTarget) setReplyTarget(reactionTarget);
          } : undefined}
          onClose={() => setReactionTarget(null)}
        />
      </KeyboardAvoidingView>

      <GifPickerBottomSheet
        visible={gifPickerVisible}
        onClose={() => setGifPickerVisible(false)}
        onSelect={gif => void handleSendGif(gif)}
      />

      <Modal visible={Boolean(gifPreviewUrl)} transparent animationType="fade" statusBarTranslucent onRequestClose={() => setGifPreviewUrl(null)}>
        <TouchableOpacity style={s.gifPreviewBackdrop} activeOpacity={1} onPress={() => setGifPreviewUrl(null)}>
          {gifPreviewUrl ? <Image source={{ uri: gifPreviewUrl }} style={s.gifPreviewImage} resizeMode="contain" /> : null}
        </TouchableOpacity>
      </Modal>

      {/* ── Sheets ──────────────────────────────── */}
      <GroupInfoSheet
        visible={groupInfoVisible}
        chatId={chatId}
        kind={kind}
        chatName={name}
        chatPhoto={photo ?? DEFAULT_COVER}
        participantIds={displayParticipantIds}
        currentUserId={currentUserId}
        onClose={() => setGroupInfoVisible(false)}
        onDeleteChat={confirmDeleteChat}
        onToggleMute={handleToggleMute}
        isMuted={isMuted}
        onLeaveSuccess={handleLeaveSuccess}
        onPeopleHere={() => setPeoHereVisible(true)}
        onDeletePlan={confirmDeletePlan}
      />

      {isGroupChat && (kind === 'event' || kind === 'plan' || kind === 'community') && (
        <PeopleHereSheet
          visible={peoHereVisible}
          chatId={chatId}
          chatKind={kind}
          participantIds={displayParticipantIds}
          currentUserId={currentUserId}
          onClose={() => setPeoHereVisible(false)}
          onVibeAccepted={(vibeChatId, otherUserId, otherName) => {
            setPeoHereVisible(false);
            showMessage({ message: t('chat.nowVibingWith').replace('{name}', otherName), type: 'success' });
            navigation.navigate('ChatDetail', {
              chatId: vibeChatId,
              name: otherName,
              kind: 'vibe',
              participantIds: [currentUserId, otherUserId],
            });
          }}
        />
      )}

      <DirectActionsSheet
        visible={actionsVisible}
        onClose={() => setActionsVisible(false)}
        onViewProfile={openOtherProfile}
        onReport={() => setReportVisible(true)}
        onBlock={confirmBlockUser}
        onDeleteChat={confirmDeleteChat}
        isMuted={isMuted}
        onToggleMute={handleToggleMute}
      />

      <ReportSheet
        visible={reportVisible}
        targetUserId={otherUserId}
        targetUserName={name}
        reportedBy={currentUserId}
        chatId={chatId}
        onClose={() => setReportVisible(false)}
        onSubmitted={() => showMessage({ message: t('chat.reportSubmitted'), type: 'success' })}
      />
      <TonyteModal
        visible={deleteChatConfirmVisible}
        title={t('chat.deleteChatQuestion')}
        message={t('chat.deleteChatMessage')}
        icon="trash-2"
        iconColor={Colors.sunset}
        buttons={[
          { label: t('common.cancel'), variant: 'secondary', onPress: () => setDeleteChatConfirmVisible(false) },
          { label: t('common.delete'), variant: 'danger', onPress: deleteCurrentChat, testID: 'delete-chat-confirm' },
        ]}
        onDismiss={() => setDeleteChatConfirmVisible(false)}
      />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const createChatListThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  messageTabs: daylightStyle(theme, {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
  }),
  messageTabActive: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: theme.colors.accentSecondary,
  }),
  // The Midnight badge is white-on-translucent, invisible on Daylight's
  // light tab bar.
  messageTabBadge: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderWidth: 1,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  messageTabBadgeText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  searchWrap: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.border,
    shadowOpacity: 0,
    elevation: 0,
  }),
  chatCardRead: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.border,
    shadowOpacity: 0,
    elevation: 0,
  }),
  chatCardUnread: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: theme.colors.accent,
    shadowOpacity: 0,
    elevation: 0,
  }),
  emptyCard: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
    shadowOpacity: 0,
    elevation: 0,
  }),
  emptyIconWrap: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  vibeRequestCard: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.accentSecondary,
    shadowOpacity: 0,
    elevation: 0,
  }),
  vibeRequestMessage: daylightStyle(theme, {
    color: theme.colors.textSecondary,
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  vibeReqLabel: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  vibeSourcePill: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  vibeSourceText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  vibeAcceptBtn: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondary,
    borderColor: '#087E7D',
  }),
  vibeAcceptText: daylightStyle(theme, { color: '#062F2F' }),
  vibeDeclineBtn: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
  }),
  vibeDeclineText: daylightStyle(theme, { color: theme.colors.textPrimary }),
  vibePreviewCancelBtn: daylightStyle(theme, {
    backgroundColor: '#FFF4F1',
    borderColor: 'rgba(201,71,71,0.42)',
  }),
  vibePreviewCancelText: daylightStyle(theme, { color: '#8F2929' }),
  alreadyVibingPill: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  alreadyVibingText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
});

const createChatDetailThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  detailRoot: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  detailHeaderWrap: daylightStyle(theme, {
    backgroundColor: theme.colors.navBackground,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  translateChatButton: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  emptyMsgCard: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
    shadowOpacity: 0,
    elevation: 0,
  }),
});

const s = StyleSheet.create({
  // Chat List
  container: { flex: 1 },
  desktopChatShell: { flex: 1, flexDirection: 'row', minWidth: 0 },
  desktopConversationRail: { width: 340, borderRightWidth: 1, minWidth: 300 },
  desktopChatCardSelected: { borderColor: '#E86619', backgroundColor: 'rgba(232,102,25,0.10)' },
  desktopChatEmpty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  desktopChatActive: { alignItems: 'stretch', justifyContent: 'flex-start', padding: 0, minWidth: 0 },
  desktopConversation: { flex: 1, minWidth: 0, minHeight: 0 },
  desktopConversationHeader: { height: 64, borderBottomWidth: 1, paddingHorizontal: 20, flexDirection: 'row', alignItems: 'center', gap: 12 },
  desktopConversationAvatar: { width: 38, height: 38, borderRadius: 19 },
  desktopConversationHeading: { flex: 1, minWidth: 0 },
  desktopConversationTitle: { fontSize: 15, fontWeight: '700' },
  desktopConversationSubtitle: { fontSize: 11, marginTop: 2 },
  desktopMessageList: { flex: 1, minHeight: 0 },
  desktopMessageContent: { paddingHorizontal: 24, paddingVertical: 22, gap: 14 },
  desktopMessageRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 9, maxWidth: '72%', alignSelf: 'flex-start' },
  desktopMessageRowMine: { alignSelf: 'flex-end' },
  desktopMessageAvatar: { width: 28, height: 28, borderRadius: 14 },
  desktopMessageColumn: { minWidth: 0 },
  desktopMessageSender: { fontSize: 10, marginBottom: 4, marginLeft: 3 },
  desktopMessageBubble: { borderWidth: 1, borderRadius: 16, borderBottomLeftRadius: 5, paddingHorizontal: 13, paddingVertical: 9 },
  desktopMessageBubbleMine: { backgroundColor: '#E86619', borderColor: '#E86619', borderBottomLeftRadius: 16, borderBottomRightRadius: 5 },
  desktopMessageText: { fontSize: 13, lineHeight: 19 },
  desktopMessageTime: { fontSize: 9, marginTop: 4, marginHorizontal: 3 },
  desktopMessageImage: { width: 220, height: 150, borderRadius: 10, marginBottom: 6 },
  desktopMessageEmpty: { minHeight: 240, alignItems: 'center', justifyContent: 'center' },
  desktopComposer: { marginHorizontal: 20, marginBottom: 18, minHeight: 52, borderWidth: 1, borderRadius: 14, paddingLeft: 15, paddingRight: 6, flexDirection: 'row', alignItems: 'center', gap: 10 },
  desktopComposerInput: { flex: 1, minWidth: 0, fontSize: 13, paddingVertical: 10, outlineStyle: 'none' } as any,
  desktopSendButton: { width: 40, height: 40, borderRadius: 11, backgroundColor: '#E86619', alignItems: 'center', justifyContent: 'center' },
  desktopSendButtonDisabled: { opacity: 0.38 },
  desktopChatEmptyIcon: { width: 58, height: 58, borderRadius: 18, borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginBottom: 14 },
  desktopChatEmptyTitle: { fontSize: 18, fontWeight: '700', marginBottom: 5 },
  desktopChatEmptySub: { maxWidth: 320, fontSize: 13, textAlign: 'center' },
  desktopDetailsRail: { width: 240, borderLeftWidth: 1, padding: 20 },
  desktopDetailsTitle: { fontSize: 14, fontWeight: '700', marginBottom: 8 },
  desktopDetailsText: { fontSize: 12, lineHeight: 18 },
  desktopDetailsAvatar: { width: 72, height: 72, borderRadius: 18, marginTop: 14, marginBottom: 10 },
  desktopDetailsName: { fontSize: 15, lineHeight: 20, fontWeight: '700', marginBottom: 14 },
  desktopDetailsMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  listHeader: {
    paddingHorizontal: Spacing.base + Spacing.xs,
    paddingTop: Spacing.sm,
    paddingBottom: Spacing.base,
  },
  listHeaderTitle: { color: Colors.textPrimary, fontSize: Typography.xl, fontWeight: Typography.bold },
  listHeaderSub: { color: 'rgba(255,255,255,0.40)', fontSize: 12, marginTop: 2 },
  messageTabs: {
    marginHorizontal: Spacing.base + Spacing.xs,
    marginBottom: Spacing.base,
    minHeight: 46,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    padding: 4,
    flexDirection: 'row',
    gap: 4,
  },
  messageTab: {
    flex: 1,
    minWidth: 0,
    borderRadius: 14,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  messageTabActive: {
    backgroundColor: 'rgba(38,208,206,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.28)',
  },
  messageTabText: {
    color: 'rgba(255,255,255,0.52)',
    fontSize: 11,
    fontWeight: Typography.bold,
    flexShrink: 1,
    textAlign: 'center',
    lineHeight: 14,
  },
  messageTabTextActive: { color: Colors.textPrimary },
  messageTabBadge: {
    position: 'absolute',
    right: 3,
    top: 3,
    minWidth: 17,
    height: 17,
    borderRadius: 8.5,
    paddingHorizontal: 4,
    backgroundColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageTabBadgeActive: { backgroundColor: Colors.turquoise },
  messageTabBadgeText: { color: 'rgba(255,255,255,0.72)', fontSize: 9, fontWeight: Typography.bold },
  messageTabBadgeTextActive: { color: '#07101d' },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginHorizontal: Spacing.base + Spacing.xs,
    marginBottom: Spacing.base,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    borderRadius: Radius.lg,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  searchInput: { flex: 1, color: Colors.textPrimary, fontSize: Typography.sm, padding: 0 },
  scroller: { flex: 1 },
  listContent: { paddingHorizontal: Spacing.base + Spacing.xs, gap: 8 },
  notificationListContent: { gap: 0, paddingHorizontal: Spacing.base },
  planRequestNotification: {
    minHeight: 76,
    marginBottom: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.2)',
    backgroundColor: 'rgba(38,208,206,0.07)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  planRequestNotificationIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(38,208,206,0.13)',
  },
  planRequestNotificationCopy: { flex: 1, minWidth: 0 },
  planRequestNotificationTitle: { color: '#fff', fontSize: 15, fontWeight: Typography.bold },
  planRequestNotificationBody: { marginTop: 4, color: 'rgba(255,255,255,0.5)', fontSize: 12, lineHeight: 17 },
  chatCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    borderRadius: Radius.lg, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 12,
  },
  chatCardRead: { backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.09)' },
  chatCardUnread: { backgroundColor: 'rgba(242,125,38,0.12)', borderColor: 'rgba(242,125,38,0.30)' },
  avatarWrap: { position: 'relative' },
  chatAvatarFrame: {
    width: 50, height: 50, borderRadius: 14, padding: 1,
    backgroundColor: 'rgba(255,255,255,0.08)',
    shadowColor: Colors.turquoise,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 8,
    elevation: 3,
  },
  chatAvatar: {
    width: 48, height: 48, borderRadius: 13,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.16)',
    backgroundColor: Colors.bgCardAlt,
  },
  unreadDot: {
    position: 'absolute', top: -4, right: -4,
    width: 12, height: 12, borderRadius: 6,
    backgroundColor: Colors.sunset, borderWidth: 2, borderColor: Colors.bg,
  },
  chatContent: { flex: 1, minWidth: 0 },
  chatTopRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
  badgeRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  badge: { borderRadius: Radius.pill, borderWidth: 1, paddingHorizontal: 6, paddingVertical: 2 },
  badgeText: { fontSize: 8, fontWeight: Typography.bold, letterSpacing: 0.8 },
  memberCountRow: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  memberCountText: { color: 'rgba(255,255,255,0.35)', fontSize: 10 },
  chatTime: { color: 'rgba(255,255,255,0.35)', fontSize: 10 },
  chatName: { fontSize: Typography.sm, fontWeight: Typography.semibold, marginBottom: 2 },
  chatNameUnread: { color: Colors.textPrimary },
  chatNameRead: { color: 'rgba(255,255,255,0.80)' },
  chatLastMsg: { fontSize: 12, lineHeight: 16 },
  chatLastMsgUnread: { color: 'rgba(255,255,255,0.86)', fontWeight: Typography.semibold },
  chatLastMsgRead: { color: 'rgba(255,255,255,0.40)' },
  emptyCard: {
    marginTop: Spacing.xl, borderRadius: Radius.xxl,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    padding: Spacing.xxxl, alignItems: 'center',
  },
  emptyIconWrap: {
    width: 48, height: 48, borderRadius: Radius.lg,
    backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center', justifyContent: 'center', marginBottom: Spacing.base,
  },
  emptyTitle: { color: 'rgba(255,255,255,0.70)', fontSize: Typography.sm, fontWeight: Typography.semibold, marginBottom: Spacing.xs, textAlign: 'center' },
  emptySubtitle: { color: 'rgba(255,255,255,0.35)', fontSize: 12, textAlign: 'center', lineHeight: 18 },
  tabLoading: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: Spacing.xl },
  notificationSectionLabel: { color: 'rgba(255,255,255,0.58)', marginTop: 14, marginBottom: 4, paddingHorizontal: 4, fontSize: 11, fontWeight: Typography.extrabold, letterSpacing: 1, textTransform: 'uppercase' },
  communityDiscoverButton: { alignSelf: 'center', marginTop: 16, minHeight: 44, paddingHorizontal: 20, borderRadius: Radius.pill, alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.purple },
  communityDiscoverText: { color: '#fff', fontSize: 14, fontWeight: Typography.extrabold },

  // Chat Detail
  detailRoot: { flex: 1, backgroundColor: Colors.bg },
  detailHeaderWrap: { zIndex: 3 },
  detailKeyboardBody: { flex: 1 },
  gifPreviewBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.88)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.base,
  },
  gifPreviewImage: {
    width: '100%',
    height: '78%',
  },
  detailHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: Spacing.base,
    paddingBottom: Spacing.base,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.08)',
    zIndex: 2,
  },
  backBtn: {
    width: 44, height: 44, borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  headerCenter: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12, minWidth: 0 },
  headerAvatar: { width: 48, height: 48, borderRadius: 16, backgroundColor: Colors.bgCardAlt, flexShrink: 0 },
  headerText: { flex: 1, minWidth: 0 },
  headerNameRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  headerName: { color: Colors.textPrimary, fontSize: 19, fontWeight: Typography.bold, lineHeight: 22 },
  headerSub: { color: 'rgba(255,255,255,0.45)', fontSize: 12, marginTop: 2 },
  chatVerifiedBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: 'rgba(38,208,206,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  optionsBtn: {
    width: 44, height: 44, borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  loadingWrap: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  blockedChatState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.xl,
    gap: Spacing.sm,
  },
  blockedChatIcon: {
    width: 52,
    height: 52,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(248,113,113,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(248,113,113,0.28)',
    marginBottom: Spacing.xs,
  },
  blockedChatTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  blockedChatText: {
    color: 'rgba(255,255,255,0.46)',
    fontSize: Typography.sm,
    lineHeight: 20,
    textAlign: 'center',
  },
  msgList: { paddingHorizontal: Spacing.base, paddingVertical: Spacing.base, gap: 12 },
  msgListEmpty: { flex: 1, justifyContent: 'center' },
  msgRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 4 },
  msgRowMe: { justifyContent: 'flex-end' },
  msgRowThem: { justifyContent: 'flex-start' },
  msgAvatar: { width: 32, height: 32, borderRadius: 12, backgroundColor: Colors.bgCardAlt, marginBottom: 20 },
  msgOuter: { maxWidth: '78%' },
  msgOuterMe: { alignItems: 'flex-end' },
  msgOuterThem: { alignItems: 'flex-start' },
  msgSenderName: { fontSize: 11, fontWeight: Typography.bold, marginBottom: 4, paddingHorizontal: 4 },
  msgSenderMe: { color: 'rgba(255,255,255,0.55)' },
  msgSenderThem: { color: 'rgba(255,255,255,0.45)' },
  msgBubble: { borderRadius: Radius.xxl, paddingHorizontal: Spacing.base, paddingVertical: 12 },
  msgBubbleMe: { backgroundColor: Colors.sunset },
  msgBubbleThem: { backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)' },
  msgText: { color: Colors.textPrimary, fontSize: Typography.sm, lineHeight: 20 },
  msgTimestamp: { color: 'rgba(255,255,255,0.60)', fontSize: 10, marginTop: 6 },
  systemPromptWrap: {
    alignItems: 'center',
    marginVertical: 4,
  },
  systemPromptCard: {
    width: '92%',
    borderRadius: Radius.xl,
    padding: Spacing.base,
    backgroundColor: 'rgba(192,132,252,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(192,132,252,0.22)',
    gap: 8,
  },
  systemPromptHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  systemPromptTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
    flex: 1,
  },
  systemPromptText: {
    color: 'rgba(255,255,255,0.74)',
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  systemPromptTime: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 10,
    textAlign: 'right',
  },
  translateChatBar: {
    paddingHorizontal: Spacing.base,
    paddingBottom: Spacing.sm,
    alignItems: 'flex-end',
  },
  translateChatButton: {
    minHeight: 34,
    borderRadius: Radius.pill,
    paddingHorizontal: Spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: 'rgba(38,208,206,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
  },
  translateChatText: {
    color: Colors.turquoise,
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
  },
  sharedEventCard: {
    borderRadius: Radius.lg,
    overflow: 'hidden',
    borderWidth: 1,
  },
  sharedEventCardMe: {
    borderColor: 'rgba(255,255,255,0.20)',
    backgroundColor: 'rgba(255,255,255,0.10)',
  },
  sharedEventCardThem: {
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  sharedEventCardSpaced: { marginTop: 10 },
  sharedEventImage: {
    width: '100%',
    height: 112,
    backgroundColor: Colors.bgCardAlt,
  },
  sharedEventImageFallback: {
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  sharedEventBody: { padding: 10, gap: 3 },
  sharedEventLabel: {
    color: 'rgba(255,255,255,0.70)',
    fontSize: 10,
    letterSpacing: 1.8,
    textTransform: 'uppercase' as const,
  },
  sharedEventTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  sharedEventMeta: { color: 'rgba(255,255,255,0.80)', fontSize: Typography.xs },
  sharedEventTeaser: { color: 'rgba(255,255,255,0.65)', fontSize: Typography.xs },
  sharedEventOpenButton: {
    marginTop: 7,
    alignSelf: 'flex-start',
    minHeight: 28,
    paddingHorizontal: 10,
    borderRadius: Radius.md,
    backgroundColor: 'rgba(255,255,255,0.12)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  sharedEventOpenText: { color: Colors.textPrimary, fontSize: Typography.xs, fontWeight: Typography.bold },
  emptyMsgCard: {
    borderRadius: Radius.xxl, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.04)', paddingHorizontal: Spacing.xl, paddingVertical: Spacing.xl,
    alignItems: 'center', marginTop: Spacing.xl,
  },
  emptyMsgText: { color: 'rgba(255,255,255,0.50)', fontSize: Typography.sm, textAlign: 'center' },
  inputRow: {
    flexDirection: 'row', alignItems: 'flex-end', gap: 12,
    paddingHorizontal: Spacing.base, paddingTop: Spacing.base,
    borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.07)',
    backgroundColor: 'rgba(7,11,20,0.92)',
  },
  msgInput: {
    flex: 1, minHeight: 52, maxHeight: 120,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)',
    borderRadius: 18,
    paddingHorizontal: Spacing.base, paddingTop: 14, paddingBottom: 14,
    color: Colors.textPrimary, fontSize: Typography.sm,
  },
  msgInputFocused: { borderColor: 'rgba(242,125,38,0.50)' },
  sendBtn: {
    width: 52, height: 52, borderRadius: 18,
    backgroundColor: Colors.sunset,
    alignItems: 'center', justifyContent: 'center',
    shadowColor: Colors.sunset, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 6,
  },
  sendBtnDisabled: { opacity: 0.45, shadowOpacity: 0 },

  // Overlay / Sheet foundations
  backdropDark: { ...StyleSheet.absoluteFill, backgroundColor: 'rgba(7,11,20,0.80)' },
  backdropDim: { ...StyleSheet.absoluteFill, backgroundColor: 'rgba(0,0,0,0.55)' },
  fullOverlay: { flex: 1, backgroundColor: 'rgba(7,11,20,0.88)' },
  sheetOverlay: { flex: 1, justifyContent: 'flex-end' },

  // GroupInfoSheet
  groupInfoScroll: { flex: 1 },
  heroWrap: { width: '100%', height: 240, overflow: 'hidden' },
  heroImage: { width: '100%', height: '100%', resizeMode: 'cover' },
  heroCloseBtn: {
    position: 'absolute', left: 16,
    width: 40, height: 40, borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center', justifyContent: 'center',
  },
  groupInfoContent: { paddingHorizontal: Spacing.base, marginTop: -Spacing.xxl, gap: Spacing.base },
  infoCard: {
    borderRadius: 28, padding: 20,
    backgroundColor: 'rgba(7,11,20,0.85)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    gap: 10,
  },
  typeBadge: {
    alignSelf: 'flex-start', borderRadius: Radius.pill,
    paddingHorizontal: 12, paddingVertical: 4,
    borderWidth: 1,
  },
  typeBadgeEvent: { backgroundColor: 'rgba(242,125,38,0.15)', borderColor: 'rgba(242,125,38,0.2)' },
  typeBadgePlan:  { backgroundColor: 'rgba(38,208,206,0.15)', borderColor: 'rgba(38,208,206,0.2)'  },
  typeBadgeText: { fontSize: 10, fontWeight: Typography.bold, letterSpacing: 1.8 },
  infoTitle: { color: Colors.textPrimary, fontSize: Typography.xl, fontWeight: Typography.bold, lineHeight: 28 },
  infoRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  infoRowText: { color: 'rgba(255,255,255,0.55)', fontSize: Typography.sm, flex: 1 },
  infoDescription: { color: 'rgba(255,255,255,0.60)', fontSize: Typography.sm, lineHeight: 20, paddingTop: 4 },
  communityDescriptionDisplay: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 28 },
  communityDescriptionPlaceholder: { color: 'rgba(255,255,255,0.36)', flex: 1 },
  communityDescriptionEditor: { gap: 8 },
  communityDescriptionInput: {
    minHeight: 96, borderRadius: 14, paddingHorizontal: 13, paddingVertical: 11,
    color: Colors.textPrimary, fontSize: Typography.sm, lineHeight: 20,
    backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(38,208,206,0.35)',
    textAlignVertical: 'top',
  },
  communityDescriptionCount: { color: 'rgba(255,255,255,0.35)', fontSize: 10, textAlign: 'right' },
  communityDescriptionActions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 18 },
  communityDescriptionCancel: { color: 'rgba(255,255,255,0.65)', fontSize: Typography.sm, fontWeight: Typography.bold },
  communityDescriptionSave: { minWidth: 70, minHeight: 36, borderRadius: 12, backgroundColor: Colors.turquoise, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 14 },
  communityDescriptionSaveText: { color: '#031014', fontSize: Typography.sm, fontWeight: Typography.bold },
  infoKindBadge: {
    alignSelf: 'flex-start', borderRadius: Radius.pill,
    paddingHorizontal: 12, paddingVertical: 4,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
  },
  infoKindText: { color: 'rgba(255,255,255,0.55)', fontSize: 11 },
  vibingSection: {
    borderRadius: 22,
    padding: 16,
    backgroundColor: 'rgba(255,255,255,0.045)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    gap: 12,
  },
  vibingHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  vibingTitle: { color: Colors.textPrimary, fontSize: Typography.base, fontWeight: Typography.bold },
  vibingSubtitle: { color: 'rgba(255,255,255,0.46)', fontSize: 12, marginTop: 3 },
  vibingIcon: {
    width: 34,
    height: 34,
    borderRadius: 14,
    backgroundColor: 'rgba(38,208,206,0.11)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.18)',
  },
  vibingScroller: { gap: 10, paddingRight: 4 },
  vibingAction: {
    minHeight: 38,
    borderRadius: 14,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(38,208,206,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.16)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  vibingActionText: { color: 'rgba(255,255,255,0.72)', fontSize: 12, fontWeight: Typography.semibold, flex: 1 },
  sectionHeading: { color: Colors.textPrimary, fontSize: Typography.base, fontWeight: Typography.bold, paddingHorizontal: 4 },
  memberCard: {
    width: 86,
    minHeight: 104,
    borderRadius: 18, padding: 10,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center', gap: 8,
  },
  memberAvatarWrap: { position: 'relative' },
  memberAvatar: { width: 52, height: 52, borderRadius: 16 },
  memberYouDot: {
    position: 'absolute', bottom: -4, right: -4,
    width: 16, height: 16, borderRadius: 8,
    backgroundColor: Colors.turquoise,
    borderWidth: 2, borderColor: '#020509',
  },
  memberName: { color: 'rgba(255,255,255,0.80)', fontSize: 12, fontWeight: Typography.semibold, textAlign: 'center' },
  membersEmpty: {
    borderRadius: 20, padding: 20,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
  },
  membersEmptyText: { color: 'rgba(255,255,255,0.40)', fontSize: Typography.sm },
  groupActions: { paddingTop: Spacing.sm, paddingBottom: Spacing.sm },
  deleteChatBtn: {
    minHeight: 52,
    borderRadius: 18,
    backgroundColor: 'rgba(248,113,113,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(248,113,113,0.28)',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
    marginBottom: Spacing.sm,
  },
  deleteChatText: { color: '#F87171', fontSize: Typography.sm, fontWeight: Typography.bold },
  leaveBtn: {
    minHeight: 52, borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center', justifyContent: 'center',
  },
  leaveBtnText: { color: 'rgba(255,255,255,0.80)', fontSize: Typography.sm, fontWeight: Typography.bold },
  leaveConfirm: { gap: 12 },
  leaveConfirmMsg: { color: 'rgba(255,255,255,0.55)', fontSize: Typography.sm, lineHeight: 20, paddingHorizontal: 4 },
  leaveConfirmRow: { flexDirection: 'row', gap: 12 },
  leaveConfirmCancel: {
    flex: 1, minHeight: 52, borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center', justifyContent: 'center',
  },
  leaveCancelText: { color: 'rgba(255,255,255,0.70)', fontSize: Typography.sm, fontWeight: Typography.bold },
  leaveConfirmLeave: {
    flex: 1, minHeight: 52, borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center', justifyContent: 'center',
  },

  // Direct Actions Sheet
  actionsSheet: {
    backgroundColor: 'rgba(12,18,30,0.97)',
    borderTopLeftRadius: 28, borderTopRightRadius: 28,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    paddingTop: 8, paddingHorizontal: 12,
  },
  actionRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    borderRadius: 16, paddingHorizontal: 12, paddingVertical: 14,
  },
  actionRowText: { color: 'rgba(255,255,255,0.85)', fontSize: Typography.sm, fontWeight: Typography.medium },
  actionDangerText: { color: '#F87171' },

  // Report Sheet
  reportSheet: {
    backgroundColor: 'rgba(10,15,25,0.97)',
    borderTopLeftRadius: 32, borderTopRightRadius: 32,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    padding: 20, gap: 16,
  },
  reportHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 },
  reportSafetyLabel: { color: 'rgba(255,255,255,0.40)', fontSize: 11, letterSpacing: 2, fontWeight: Typography.bold },
  reportTitle: { color: Colors.textPrimary, fontSize: Typography.xl, fontWeight: Typography.bold, marginTop: 4 },
  sheetCloseBtn: {
    width: 40, height: 40, borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  reasonsGrid: { gap: 8 },
  reasonBtn: {
    borderRadius: Radius.lg, paddingHorizontal: Spacing.base, paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)',
  },
  reasonBtnSelected: { backgroundColor: Colors.sunset, borderColor: Colors.sunset },
  reasonBtnText: { color: 'rgba(255,255,255,0.80)', fontSize: Typography.sm, fontWeight: Typography.medium },
  reasonBtnTextSelected: { color: '#fff' },
  reportDetails: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)',
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.base, paddingVertical: 12,
    color: Colors.textPrimary, fontSize: Typography.sm,
    height: 96,
  },
  reportError: { color: Colors.sunset, fontSize: Typography.sm },
  reportBtnRow: { flexDirection: 'row', gap: 12 },
  reportCancelBtn: {
    flex: 1, paddingVertical: 14, borderRadius: Radius.lg,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)',
    alignItems: 'center', justifyContent: 'center',
  },
  reportCancelText: { color: Colors.textPrimary, fontSize: Typography.sm, fontWeight: Typography.bold },
  reportSubmitBtn: {
    flex: 1, paddingVertical: 14, borderRadius: Radius.lg,
    backgroundColor: Colors.sunset,
    alignItems: 'center', justifyContent: 'center',
  },
  reportSubmitText: { color: '#fff', fontSize: Typography.sm, fontWeight: Typography.bold },

  // Vibe Requests Inbox
  vibeInbox: {
    marginHorizontal: Spacing.base + Spacing.xs,
    marginBottom: Spacing.base,
    borderRadius: 20,
    backgroundColor: 'rgba(38,208,206,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.18)',
    overflow: 'hidden',
  },
  vibeInboxHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,208,206,0.12)',
  },
  vibeInboxTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
    flex: 1,
  },
  vibeInboxBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: Colors.turquoise,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  vibeInboxBadgeText: { color: '#07101d', fontSize: 11, fontWeight: Typography.bold },
  vibeReqCard: {
    marginHorizontal: 4,
    marginVertical: 6,
    paddingHorizontal: 14,
    paddingVertical: 13,
    borderRadius: 18,
    borderWidth: 1.4,
    borderColor: 'rgba(38,208,206,0.58)',
    backgroundColor: 'rgba(13,30,40,0.72)',
    shadowColor: Colors.turquoise,
    shadowOpacity: 0.24,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
    overflow: 'hidden',
  },
  vibeReqCardPressed: {
    borderColor: 'rgba(38,208,206,0.82)',
    backgroundColor: 'rgba(18,42,52,0.86)',
    transform: [{ scale: 0.992 }],
  },
  vibeReqAccent: {
    position: 'absolute',
    left: 0,
    top: 10,
    bottom: 10,
    width: 3,
    borderTopRightRadius: 3,
    borderBottomRightRadius: 3,
    backgroundColor: Colors.turquoise,
    opacity: 0.95,
  },
  vibeReqMainRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  vibeReqAvatarWrap: {
    width: 50,
    height: 50,
    borderRadius: 17,
    padding: 2,
    backgroundColor: 'rgba(38,208,206,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.44)',
    flexShrink: 0,
  },
  vibeReqAvatar: { width: '100%', height: '100%', borderRadius: 14, backgroundColor: Colors.bgCardAlt },
  vibeReqContent: { flex: 1, minWidth: 0, gap: 6, paddingTop: 1 },
  vibeReqNameRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 0, minWidth: 0 },
  vibeReqName: { color: 'rgba(255,255,255,0.96)', fontSize: Typography.sm, fontWeight: Typography.bold, flex: 1, minWidth: 0 },
  vibeReqBadge: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: 'rgba(38,208,206,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  vibeReqTime: { color: 'rgba(226,232,240,0.7)', fontSize: 11, flexShrink: 0 },
  vibeReqLabel: { color: '#A8F4EF', fontSize: 12, lineHeight: 17, fontWeight: Typography.bold },
  vibeReqMessage: {
    color: 'rgba(255,255,255,0.92)',
    fontSize: 12,
    lineHeight: 18,
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.075)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  vibeSourcePill: {
    alignSelf: 'flex-start',
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    minHeight: 24,
    borderRadius: Radius.pill,
    paddingHorizontal: 9,
    backgroundColor: 'rgba(38,208,206,0.13)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.28)',
  },
  vibeSourceText: { color: 'rgba(224,242,254,0.88)', fontSize: 11, fontWeight: Typography.medium, flexShrink: 1, minWidth: 0 },
  vibeReqActions: { flexDirection: 'row', gap: 8, alignSelf: 'flex-end', alignItems: 'center', marginTop: 10 },
  vibeAcceptBtn: {
    minWidth: 62,
    minHeight: 36,
    paddingHorizontal: 12,
    borderRadius: 13,
    backgroundColor: 'rgba(38,208,206,0.27)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.68)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  vibeAcceptText: { color: '#BFF9F5', fontSize: 12, fontWeight: Typography.bold },
  vibeDeclineBtn: {
    minWidth: 62,
    minHeight: 36,
    paddingHorizontal: 12,
    borderRadius: 13,
    backgroundColor: 'rgba(255,255,255,0.09)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.20)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  vibeDeclineText: { color: 'rgba(255,255,255,0.76)', fontSize: 12, fontWeight: Typography.semibold },
  vibeCancelBtnDaylight: {
    minWidth: 84,
    backgroundColor: '#FCE4DE',
    borderColor: '#B84A42',
    borderWidth: 1.5,
  },
  vibeCancelTextDaylight: { color: '#7C201D' },
  alreadyVibingPill: {
    minHeight: 34,
    borderRadius: Radius.md,
    paddingHorizontal: 10,
    backgroundColor: 'rgba(38,208,206,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  alreadyVibingText: { color: '#BFF9F5', fontSize: 11, fontWeight: Typography.bold },
  vibeActionDisabled: { opacity: 0.52 },
  vibePreviewRoot: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  vibePreviewSheetWrap: {
    justifyContent: 'flex-end',
    maxHeight: '88%',
  },
  vibePreviewSheet: {
    maxHeight: '100%',
    marginHorizontal: 10,
    marginBottom: 10,
    borderRadius: 28,
    overflow: 'hidden',
    backgroundColor: 'rgba(7,14,24,0.98)',
    borderWidth: 1.4,
    borderColor: 'rgba(38,208,206,0.48)',
    shadowColor: Colors.turquoise,
    shadowOpacity: 0.28,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: -8 },
    elevation: 14,
  },
  sheetHandle: {
    alignSelf: 'center',
    width: 42,
    height: 4,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.28)',
    marginTop: 10,
    marginBottom: 6,
  },
  vibePreviewScroll: {
    paddingHorizontal: 18,
    paddingTop: 10,
    paddingBottom: 14,
    gap: 14,
  },
  vibePreviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  vibePreviewAvatarWrap: { width: 76, height: 76 },
  vibePreviewAvatar: {
    width: 76,
    height: 76,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: 'rgba(38,208,206,0.72)',
    backgroundColor: Colors.bgCardAlt,
  },
  vibePreviewTitleBlock: { flex: 1, minWidth: 0, gap: 4 },
  vibePreviewNameRow: { gap: 7, alignItems: 'flex-start' },
  vibePreviewName: { color: '#fff', fontSize: 22, lineHeight: 26, fontWeight: Typography.bold },
  vibePreviewMeta: { color: 'rgba(226,232,240,0.62)', fontSize: 12, lineHeight: 17 },
  vibePreviewBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    minHeight: 24,
    alignSelf: 'flex-start',
    borderRadius: Radius.pill,
    paddingHorizontal: 9,
    backgroundColor: 'rgba(38,208,206,0.13)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.34)',
  },
  vibePreviewBadgeText: { color: '#BFF9F5', fontSize: 11, fontWeight: Typography.bold },
  vibePreviewSection: {
    gap: 7,
    borderRadius: 16,
    padding: 12,
    backgroundColor: 'rgba(255,255,255,0.055)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
  },
  vibePreviewSectionLabel: { color: Colors.turquoise, fontSize: 11, fontWeight: Typography.bold, textTransform: 'uppercase' },
  vibePreviewBody: { color: 'rgba(255,255,255,0.88)', fontSize: 13, lineHeight: 19 },
  vibePreviewMessage: { color: '#fff', fontSize: 14, lineHeight: 20, fontWeight: Typography.medium },
  vibePreviewChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  vibePreviewChip: {
    borderRadius: Radius.pill,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: 'rgba(38,208,206,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
  },
  vibePreviewChipText: { color: 'rgba(224,242,254,0.9)', fontSize: 12, fontWeight: Typography.medium },
  vibePreviewSource: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    borderRadius: 16,
    padding: 12,
    backgroundColor: 'rgba(38,208,206,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.26)',
  },
  vibePreviewSourceText: { color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: Typography.semibold, flex: 1, minWidth: 0 },
  vibePreviewProfileBtn: {
    minHeight: 38,
    borderRadius: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  vibePreviewProfileText: { color: 'rgba(255,255,255,0.82)', fontSize: 13, fontWeight: Typography.semibold },
  vibePreviewActions: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 18,
    paddingTop: 12,
    paddingBottom: Platform.OS === 'android' ? 16 : 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(5,10,18,0.96)',
  },
  vibePreviewPassBtn: {
    flex: 1,
    minHeight: 48,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.09)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  vibePreviewVibeBtn: {
    flex: 1,
    minHeight: 48,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(38,208,206,0.28)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.70)',
  },
  vibePreviewFullBtn: { flex: 1 },
  vibePreviewAcceptedPill: {
    flex: 1,
    minHeight: 48,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(38,208,206,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.32)',
  },
  vibePreviewPassText: { color: 'rgba(255,255,255,0.78)', fontSize: 14, fontWeight: Typography.bold },
  vibePreviewVibeText: { color: '#D8FFFC', fontSize: 14, fontWeight: Typography.bold },

  // People Here Button (in GroupInfoSheet)
  peopleHereBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderRadius: 20,
    padding: 16,
    backgroundColor: 'rgba(38,208,206,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.18)',
  },
  peopleHereBtnIcon: {
    width: 40,
    height: 40,
    borderRadius: 16,
    backgroundColor: 'rgba(38,208,206,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  peopleHereBtnText: { flex: 1 },
  peopleHereBtnTitle: { color: Colors.textPrimary, fontSize: Typography.sm, fontWeight: Typography.bold },
  peopleHereBtnSub: { color: 'rgba(255,255,255,0.45)', fontSize: 12, marginTop: 2 },

  // People Here Sheet
  peopleHereSheet: {
    flex: 1,
    backgroundColor: 'rgba(7,11,20,0.97)',
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    overflow: 'hidden',
  },
  peopleHereHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.07)',
  },
  peopleHereLabel: { color: 'rgba(255,255,255,0.40)', fontSize: 11, letterSpacing: 2, fontWeight: Typography.bold },
  peopleHereTitle: { color: Colors.textPrimary, fontSize: Typography.xl, fontWeight: Typography.bold, marginTop: 4 },
  peopleHereLoading: { padding: 32, alignItems: 'center', gap: 10 },
  peopleHereErrorIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  peopleHereErrorText: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.sm,
    textAlign: 'center',
    lineHeight: 20,
  },
  peopleHereEmptySub: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 18,
    paddingHorizontal: 16,
  },
  peopleHereRetryBtn: {
    marginTop: 4,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: Radius.lg,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  peopleHereRetryText: { color: Colors.textPrimary, fontSize: Typography.sm, fontWeight: Typography.semibold },
  peopleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderRadius: 20,
    padding: 14,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.07)',
  },
  peopleAvatarWrap: { width: 52, height: 52, flexShrink: 0 },
  peopleAvatar: { width: 52, height: 52, borderRadius: 16, backgroundColor: Colors.bgCardAlt, flexShrink: 0 },
  peopleInfo: { flex: 1, minWidth: 0, gap: 4 },
  peopleNameRow: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  peopleName: { color: Colors.textPrimary, fontSize: Typography.sm, fontWeight: Typography.bold, flex: 1 },
  communityRoleBadge: { borderRadius: Radius.pill, paddingHorizontal: 7, paddingVertical: 2, backgroundColor: 'rgba(38,208,206,0.12)', borderWidth: 1, borderColor: 'rgba(38,208,206,0.25)' },
  communityRoleText: { color: Colors.turquoise, fontSize: 9, fontWeight: Typography.bold },
  peopleVerifiedBadge: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: 'rgba(38,208,206,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  peopleBio: { color: 'rgba(255,255,255,0.50)', fontSize: 12, lineHeight: 17 },
  peopleTags: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  peopleTag: {
    borderRadius: Radius.pill,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peopleTagText: { color: 'rgba(255,255,255,0.55)', fontSize: 11 },
  peopleAction: { flexShrink: 0, alignItems: 'flex-end' },
  peopleRemoveBtn: { flexShrink: 0, paddingLeft: 8, paddingVertical: 4, justifyContent: 'center', alignItems: 'center' },
  vibeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: Radius.md,
    backgroundColor: 'rgba(38,208,206,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.28)',
  },
  vibeBtnText: { color: Colors.turquoise, fontSize: 12, fontWeight: Typography.bold },
  vibingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: Radius.md,
    backgroundColor: 'rgba(38,208,206,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.20)',
  },
  vibingText: { color: Colors.turquoise, fontSize: 12, fontWeight: Typography.semibold },
  sentBadge: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: Radius.md,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
  },
  sentText: { color: 'rgba(255,255,255,0.40)', fontSize: 12, fontWeight: Typography.medium },
  vibeMessageOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(2,5,9,0.68)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.base,
  },
  vibeMessageKeyboard: {
    width: '100%',
    alignItems: 'center',
  },
  vibeMessageDialog: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 28,
    padding: 20,
    backgroundColor: 'rgba(10,15,25,0.98)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 18 },
    shadowOpacity: 0.35,
    shadowRadius: 28,
    elevation: 18,
  },
  vibeMessageIcon: {
    width: 42,
    height: 42,
    borderRadius: 16,
    backgroundColor: 'rgba(38,208,206,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  vibeMessageTitle: { color: Colors.textPrimary, fontSize: Typography.xl, fontWeight: Typography.bold },
  vibeMessageSub: { color: 'rgba(255,255,255,0.55)', fontSize: Typography.sm, lineHeight: 20, marginTop: 6 },
  vibeMessageInput: {
    minHeight: 96,
    maxHeight: 140,
    marginTop: 16,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.055)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
    paddingHorizontal: 14,
    paddingTop: 13,
    paddingBottom: 13,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    textAlignVertical: 'top',
  },
  vibeMessageCount: { color: 'rgba(255,255,255,0.32)', fontSize: 11, textAlign: 'right', marginTop: 6 },
  vibeMessageActions: { flexDirection: 'row', gap: 10, marginTop: 16 },
  vibeMessageCancel: {
    flex: 1,
    minHeight: 48,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  vibeMessageCancelText: { color: 'rgba(255,255,255,0.72)', fontSize: Typography.sm, fontWeight: Typography.bold },
  vibeMessageSend: {
    flex: 1,
    minHeight: 48,
    borderRadius: 16,
    backgroundColor: Colors.turquoise,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vibeMessageSendDisabled: { opacity: 0.55 },
  vibeMessageSendText: { color: '#07101d', fontSize: Typography.sm, fontWeight: Typography.bold },
});

export default ChatListScreen;
